

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { usePermission } from '../../auth/usePermission';
import { useModuleEntitlements } from '../../hooks/useModuleEntitlements';
import { useTravelUi } from '../../hooks/useTravelUi';
import CallHistoryCard from 'components/CallRecording/CallHistoryCard';
import { getApiErrorMessage, isModuleNotEntitledError } from '../../utils/apiErrors';
import {
  Card,
  Grid,
  Divider,
  Modal,
  Box,
  TextField,
  CircularProgress,
  Select,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Chip,
  Tooltip,
  Autocomplete,
  Snackbar,
  Skeleton,
  Avatar,
} from "@mui/material";
import colors from "assets/theme/base/colors";
import Alert from '@mui/material/Alert';
import { Delete, FileCopy, Close, Edit, Save, ThreeSixty} from "@mui/icons-material";
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { alpha } from '@mui/material/styles';
import Icon from "@mui/material/Icon";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import FolderIcon from '@mui/icons-material/Folder';
import AttachmentIcon from '@mui/icons-material/Attachment';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';


// Import custom components - make sure these paths are correct
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CallContactMenu from "components/Softphone/CallContactMenu";
import { commonIconButtonStyles } from 'assets/theme/components/iconButtonStyles';
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import AddContactModal from 'components/AddContactModal';
import AttachmentUploadModal from 'components/CustomModals/AttachmentUploadModal';
import Attachments from 'layouts/dashboard/components/Attachments';
import ContactCard from './ContactCard';
import {formatDate} from 'utils/DateTimeConversion';
import { useTheme } from '@mui/material/styles';
import Account360V2 from './account360/Account360V2';
import Activities from 'layouts/dashboard/components/Activities';
import ActivityModal from 'components/CustomModals/CustomActivityModal';
import { useSnackbar } from 'notistack';
import OrderModal from 'components/CustomModals/AddOrderModal';
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import AddCaseModal from 'components/CustomModals/AddCaseModal';
import { Email } from '@mui/icons-material';
import EmailModal from 'components/EmailModal/index.js';
import UnauthorizedAccess from 'components/StyledAccessDenied';
import { useCurrency } from '../../context/CurrencyContext';
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';
import { frontendLogger } from '../../utils/logger';
import { downloadPrivateObject } from 'utils/S3';

import EchoComposer from 'components/EchoComposer/EchoComposer';
import MDUserAvatar from 'components/MDUserAvatar';
import NewMerchantAssetModal from 'components/NewMerchantAssetModal';
import NewMerchantAllocationModal from 'components/NewMerchantAllocationModal';
import { CountrySelect, StateSelect, CitySelect } from 'components/GeographySelect';
import AddressAutocompleteInput from 'layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput';
import AccountFormModal from 'components/NewAccountForm';
import { RecordCallProvider } from "components/v2/RecordCallContext";
import { UiDetailSection } from "components/v2/record/DetailSection";

// Add AccountDetailsSkeleton component
const AccountDetailsSkeleton = () => (
  <MDBox p={3}>
    <Grid container spacing={3}>
      {/* Simulate Basic Information section */}
      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-field-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={40} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
      </Grid>

      {/* Simulate Financial Information section */}
      {[...Array(3)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-financial-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={40} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
      </Grid>

      {/* Simulate Billing Address section */}
      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-billing-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={40} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
      </Grid>

      {/* Simulate Shipping Address section */}
      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-shipping-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={40} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
      </Grid>

      {/* Simulate Audit Information section */}
      {[...Array(4)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-audit-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={40} />
        </Grid>
      ))}
    </Grid>
  </MDBox>
);

// import Customer360View from 'components/Customer360View/Customer360View';

// import { handleSaveCase } from 'components/CustomModals/AddCaseModal';


function AccountDetail() {
  const { user, axiosInstance } = useAuth();
  const { isSupportUser } = usePermission('accounts');
  // UI v2 record layout (design-reference "Sales · Deal record").
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const hasTasksModule = isModuleEnabled('TASKS');
  // Travel UI needs the TRAVEL edition and the company travel toggle.
  const { showTravel, loading: travelUiLoading } = useTravelUi();
  const [users, setUsers] = useState({}); // Add this to store user data
  const navigate = useNavigate();
  const { id } = useParams();
  const [account, setAccount] = useState(null);
  // Wait for travel UI to resolve so we do not briefly fetch legacy orders.
  // v2 keeps legacy orders for travel tenants (Sales app); classic hides them.
  const showTravelMerchantSections = !travelUiLoading && showTravel;
  const showLegacyOrders = !travelUiLoading && (!showTravel || isV2);
  const [contacts, setContacts] = useState([]);

  // Everything reachable from this account: its own switchboard number, then
  // each related contact. The picker dedupes and collapses to a plain dial
  // button when only one of these actually has a number.
  const callTargets = useMemo(() => {
    const accountNumber = account?.mobile || account?.phone;
    const targets = accountNumber
      ? [{ id: `account-${account?.id}`, name: account?.name || 'Account', label: 'Main', phone: accountNumber }]
      : [];
    (contacts || []).forEach((c) => {
      const phone = c?.mobile || c?.details?.phone;
      if (!phone) return;
      targets.push({
        id: `contact-${c?.details?.id}`,
        name: c?.name || 'Contact',
        label: c?.details?.title || 'Contact',
        phone,
        contactId: c?.details?.id,
      });
    });
    return targets;
  }, [account, contacts]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});
  const [partnerTypeOptions, setPartnerTypeOptions] = useState(["Merchant", "Guide", "Influencer", "Affiliate", "Other"]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState(["Accommodation", "Transport", "Transfer", "Activity", "Meals", "Guide", "DMC"]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [nameError, setNameError] = useState('');
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [managers, setManagers] = useState([]);
  const [formErrors, setFormErrors] = useState({}); // Add formErrors state
  const [agents, setAgents] = useState([]);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [newActivity, setNewActivity] = useState({
    account_id: id,
    type: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [deals, setDeals] = useState([]); // State to hold deals
  const [isDealModalOpen, setIsDealModalOpen] = useState(false); // State to manage deal modal
  const [newDealData, setNewDealData] = useState({
    account_id: id,
    user_id: user.id, // Add user ID to initial state
    name: account?.name, // Provide a default name
    value: '',
    stage: 'Qualification',
    probability: 0,
  });

  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [newOrder, setNewOrder] = useState({
    // deal_id: id,
    total_amount: 0,
    status: 'Sent',
  })

  const [isCaseModalOpen, setIsCaseModalOpen] = useState(false);
  const [cases, setCases] = useState([]);
  const [newCase, setNewCase] = useState([]);
  const [merchantAssets, setMerchantAssets] = useState([]);
  const [isMerchantAssetModalOpen, setIsMerchantAssetModalOpen] = useState(false);
  const [isMerchantAssetsLoading, setIsMerchantAssetsLoading] = useState(false);
  const [merchantAllocations, setMerchantAllocations] = useState([]);
  const [isMerchantAllocationModalOpen, setIsMerchantAllocationModalOpen] = useState(false);
  const [isMerchantAllocationsLoading, setIsMerchantAllocationsLoading] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const customFieldsRef = useRef(null); // Added to state declarations
  // const { handleSaveCase } = AddCaseModal();
  // handleSaveCase(event);
  const { currencies } = useCurrencyCodes();
  const defaultCurrency = useMemo(
    () => currencies.find(c => c.code === 'INR') || currencies[0] || null,
    [currencies]
  );
  const [currency, setCurrency] = useState(defaultCurrency);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    content: ''
  });
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [emailContext, setEmailContext] = useState({});
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCloneLoading, setIsCloneLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [is360Loading, setIs360Loading] = useState(false);
  const [isDealLoading, setIsDealLoading] = useState(false);
  const [isCaseLoading, setIsCaseLoading] = useState(false);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);
  const [isCloneButtonLoading, setIsCloneButtonLoading] = useState(false);
  const { formatCurrency, currency: systemCurrency, getCurrencyLabel } = useCurrency();
  const [sectionOrder, setSectionOrder] = useState([]);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false); // Add this new state

  // State for cached names
  const [nameCache, setNameCache] = useState({});

  // State for feed items
  const [feedItems, setFeedItems] = useState([]);
  const [isFeedLoading, setIsFeedLoading] = useState(false);

  // State for editing feed items
  const [editingFeedItem, setEditingFeedItem] = useState(null);
  const [editFeedContent, setEditFeedContent] = useState('');
  const [isEditingFeed, setIsEditingFeed] = useState(false);

  // State for echo modal
  const [isEchoModalOpen, setIsEchoModalOpen] = useState(false);
  const [newEchoContent, setNewEchoContent] = useState('');
  const [isCreatingEcho, setIsCreatingEcho] = useState(false);

  // State for delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const cloneAnimationKeyframes = `
  @keyframes cloneAnimation {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [customFields, setCustomFields] = useState([]);

  // Function to fetch lead name
  const fetchLeadName = async (leadId) => {
    if (nameCache[`lead_${leadId}`]) {
      return nameCache[`lead_${leadId}`];
    }

    try {
      const response = await axiosInstance.get(`/leads/${leadId}`);
      const leadName = response.data.full_lead_name || `Lead #${leadId}`;
      setNameCache(prev => ({ ...prev, [`lead_${leadId}`]: leadName }));
      return leadName;
    } catch (error) {
      console.error('Error fetching lead name:', error);
      return `Lead #${leadId}`;
    }
  };

  // Function to fetch account name
  const fetchAccountName = async (accountId) => {
    if (nameCache[`account_${accountId}`]) {
      return nameCache[`account_${accountId}`];
    }

    try {
      const response = await axiosInstance.get(`/accounts/${accountId}`);
      const accountName = response.data.name || `Account #${accountId}`;
      setNameCache(prev => ({ ...prev, [`account_${accountId}`]: accountName }));
      return accountName;
    } catch (error) {
      console.error('Error fetching account name:', error);
      return `Account #${accountId}`;
    }
  };

  // Function to fetch deal name
  const fetchDealName = async (dealId) => {
    if (nameCache[`deal_${dealId}`]) {
      return nameCache[`deal_${dealId}`];
    }

    try {
      const response = await axiosInstance.get(`/deals/${dealId}`);
      const dealName = response.data.name || `Deal #${dealId}`;
      setNameCache(prev => ({ ...prev, [`deal_${dealId}`]: dealName }));
      return dealName;
    } catch (error) {
      console.error('Error fetching deal name:', error);
      return `Deal #${dealId}`;
    }
  };

  // Function to fetch contact name
  const fetchContactName = async (contactId) => {
    if (nameCache[`contact_${contactId}`]) {
      return nameCache[`contact_${contactId}`];
    }

    try {
      const response = await axiosInstance.get(`/contacts/${contactId}`);
      const contactName = `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim() || `Contact #${contactId}`;
      setNameCache(prev => ({ ...prev, [`contact_${contactId}`]: contactName }));
      return contactName;
    } catch (error) {
      console.error('Error fetching contact name:', error);
      return `Contact #${contactId}`;
    }
  };

  // Function to fetch entity name based on type
  const fetchEntityName = async (entityType, entityId) => {
    switch (entityType.toLowerCase()) {
      case 'lead':
        return await fetchLeadName(entityId);
      case 'account':
        return await fetchAccountName(entityId);
      case 'deal':
        return await fetchDealName(entityId);
      case 'contact':
        return await fetchContactName(entityId);
      case 'order':
        return await fetchOrderName(entityId);
      case 'invoice':
        return await fetchInvoiceName(entityId);
      case 'quote':
        return await fetchQuoteName(entityId);
      case 'product':
        return await fetchProductName(entityId);
      case 'case':
        return await fetchCaseName(entityId);
      default:
        return `${entityType} #${entityId}`;
    }
  };

  // Function to fetch user name
  const fetchUserName = async (userId) => {
    if (nameCache[`user_${userId}`]) {
      return nameCache[`user_${userId}`];
    }

    try {
      const response = await axiosInstance.get(`/users/${userId}`);
      const userName = response.data.fullname || response.data.full_name || response.data.username;
      setNameCache(prev => ({ ...prev, [`user_${userId}`]: userName }));
      return userName;
    } catch (error) {
      console.error('Error fetching user name:', error);
      return `User ${userId}`;
    }
  };

  // Function to fetch order name
  const fetchOrderName = async (orderId) => {
    if (nameCache[`order_${orderId}`]) {
      return nameCache[`order_${orderId}`];
    }

    try {
      const response = await axiosInstance.get(`/orders/${orderId}`);
      const orderName = response.data.order_number || `Order #${orderId}`;
      setNameCache(prev => ({ ...prev, [`order_${orderId}`]: orderName }));
      return orderName;
    } catch (error) {
      console.error('Error fetching order name:', error);
      return `Order #${orderId}`;
    }
  };

  // Function to fetch invoice name
  const fetchInvoiceName = async (invoiceId) => {
    if (nameCache[`invoice_${invoiceId}`]) {
      return nameCache[`invoice_${invoiceId}`];
    }

    try {
      const response = await axiosInstance.get(`/invoices/${invoiceId}`);
      const invoiceName = `Invoice #${response.data.invoice_number || invoiceId}`;
      setNameCache(prev => ({ ...prev, [`invoice_${invoiceId}`]: invoiceName }));
      return invoiceName;
    } catch (error) {
      console.error('Error fetching invoice name:', error);
      return `Invoice #${invoiceId}`;
    }
  };

  // Function to fetch quote name
  const fetchQuoteName = async (quoteId) => {
    if (nameCache[`quote_${quoteId}`]) {
      return nameCache[`quote_${quoteId}`];
    }

    try {
      const response = await axiosInstance.get(`/quotations/${quoteId}`);
      const quoteName = response.data.quote_name || `Quote #${quoteId}`;
      setNameCache(prev => ({ ...prev, [`quote_${quoteId}`]: quoteName }));
      return quoteName;
    } catch (error) {
      console.error('Error fetching quote name:', error);
      return `Quote #${quoteId}`;
    }
  };

  // Function to fetch product name
  const fetchProductName = async (productId) => {
    if (nameCache[`product_${productId}`]) {
      return nameCache[`product_${productId}`];
    }

    try {
      const response = await axiosInstance.get(`/products/${productId}`);
      const productName = response.data.name || `Product #${productId}`;
      setNameCache(prev => ({ ...prev, [`product_${productId}`]: productName }));
      return productName;
    } catch (error) {
      console.error('Error fetching product name:', error);
      return `Product #${productId}`;
    }
  };

  // Function to fetch case name
  const fetchCaseName = async (caseId) => {
    if (nameCache[`case_${caseId}`]) {
      return nameCache[`case_${caseId}`];
    }

    try {
      const response = await axiosInstance.get(`/cases/${caseId}`);
      const caseName = response.data.case_number || `Case #${caseId}`;
      setNameCache(prev => ({ ...prev, [`case_${caseId}`]: caseName }));
      return caseName;
    } catch (error) {
      console.error('Error fetching case name:', error);
      return `Case #${caseId}`;
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axiosInstance.get(`/users/`);
      const managersData = response.data.map(user => ({
        id: user.id,
        username: user.fullname,
      }));

      setManagers(managersData); // Set managers to an array of objects
      // console.log('managers:', managersData);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };



  useEffect(() => {
    fetchAccountData();
    fetchContacts();
    fetchManagers();
    fetchAgents();
    fetchAttachments();
    fetchDeals();
    fetchCases();
    fetchFeedItems(); // Add feed items fetch
    fetchPartnerOptions();
  }, [id]);

  // Legacy orders only when travel UI is off (travel uses merchant sections instead).
  useEffect(() => {
    if (!id || travelUiLoading) return;
    if (!showLegacyOrders) {
      setOrders([]);
      setIsOrderModalOpen(false);
      return;
    }
    fetchOrders();
  }, [id, showLegacyOrders, travelUiLoading]);

  // Activities require TASKS module entitlement
  useEffect(() => {
    if (entitlementsLoading || !id) return;
    if (hasTasksModule) {
      fetchActivities();
    } else {
      setActivities([]);
    }
  }, [id, entitlementsLoading, hasTasksModule]);

  useEffect(() => {
    if (travelUiLoading || !id) return;
    if (showTravelMerchantSections) {
      fetchMerchantAssets();
      fetchMerchantAllocations();
    } else {
      setMerchantAssets([]);
      setMerchantAllocations([]);
      setIsMerchantAssetModalOpen(false);
      setIsMerchantAllocationModalOpen(false);
    }
  }, [id, showTravelMerchantSections, travelUiLoading]);

  const fetchPartnerOptions = async () => {
    try {
      const response = await axiosInstance.get('/accounts/partner-options');
      if (response.data?.partner_types?.length) {
        setPartnerTypeOptions(response.data.partner_types);
      }
      if (response.data?.service_types?.length) {
        setServiceTypeOptions(response.data.service_types);
      }
    } catch (error) {
      console.error('Error fetching partner options:', error);
    }
  };

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const response = await axiosInstance.get(`/accounts/${id}`);
        setAccountData(response.data);
      } catch (error) {
        console.error('Error fetching account:', error);
      }
    };
    fetchAccountData();
  }, [id]);

  const fetchDeals = async () => {
    try {
      const response = await axiosInstance.get(`/accounts/${id}/deals`);
      setDeals(response.data || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
    }
  };

  const handleOpenDealModal = async () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create deals.',
        severity: 'error',
      });
      return;
    }
    setIsDealLoading(true);
    try {
      await handleCreateDeal();
    } catch (error) {
      console.error('Error in handleOpenDealModal:', error);
      setSnackbar({
        open: true,
        message: 'Failed to initiate deal creation.',
        severity: 'error',
      });
    } finally {
      setIsDealLoading(false);
    }
  };

  const handleCloseDealModal = () => {
    setIsDealModalOpen(false);
    setNewDealData({ account_id: id, name: '', value: 0, stage: '', probability: 0 }); // Reset data
  };

  const handleDealDataChange = (e) => {
    const { name, value } = e.target;

    // Handle numeric fields
    const numericFields = ['value', 'probability'];
    const processedValue = numericFields.includes(name)
      ? (value === '' ? '' : parseFloat(value))
      : value;

    setNewDealData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleCreateDeal = async () => {
    try {
      //console.log('Creating deal for account:', id); // Debug log

      const calculateEstimatedClosureDate = () => {
        const currentDate = new Date();
        const estimatedClosureDate = new Date(currentDate);
        estimatedClosureDate.setMonth(currentDate.getMonth() + 1);
        return estimatedClosureDate.toISOString();
      };

      const dealPayload = {
        account_id: id,
        account_name: account.name,
        contact_name: account.contact_name,
        user_id: user.id,
        name: account.name, // Use account name as deal name
        value: 0, // Default value
        probability: 0, // Default probability
        stage: 'Qualification', // Default stage
        estimated_closure_date: calculateEstimatedClosureDate(),
        currency: account.currency || 'INR', // Use account currency or default to INR
        // assigned_to: user.fullname,
      };

      //console.log('Deal payload:', dealPayload); // Debug log

      const response = await axiosInstance.post('/deals/', dealPayload);
      //console.log('Deal created:', response.data); // Debug log

      // Log the deal creation with correct parameters
      await frontendLogger.logCreate('Deal', {
        id: response.data.id,
        account_id: id,
        account_name: account.name,
        name: dealPayload.name,
        stage: dealPayload.stage,
        value: dealPayload.value,
        probability: dealPayload.probability,
        currency: dealPayload.currency,
        created_by: user.id,
        user: user.username,
        created_at: response.data.created_at || new Date().toISOString()
      });

      // Navigate to deal detail page in editing mode
      navigate(`/deals/${response.data.id}?edit=true`);

      enqueueSnackbar('Deal created successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Full Error Object:', error);
      const errorMessage = error.response?.data?.detail?.[0]?.msg ||
        error.response?.data?.message ||
        'Error creating deal. Please try again.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };


  const fetchAccountData = async () => {
    setIsDetailsLoading(true);
    try {
      const response = await axiosInstance.get(`/accounts/${id}`);
      const accountData = response.data;

      // If the API returns custom fields in a separate object, merge them into the main data
      if (accountData.custom_fields) {
        Object.entries(accountData.custom_fields).forEach(([key, value]) => {
          accountData[key] = value;
        });
      }

      //console.log('Processed account data:', accountData);
      setAccount(accountData);
    } catch (error) {
      console.error('Error initializing page:', error);
      if (error.response?.status === 403) {
        setIsUnauthorized(true);
        setSnackbar({
          open: true,
          message: "You don't have permission to access this Account",
          severity: 'error',
        });
        // Optionally redirect after a delay
        setTimeout(() => {
          navigate('/accounts');
        }, 3000);
      } else {
        setSnackbar({
          open: true,
          message: 'Error loading Account data. Please try again later.',
          severity: 'error',
        });
      }
    } finally {
      setIsDetailsLoading(false);
    }
  };
  const formatStateCode = (value) => {
    if (value === null || value === undefined || value === '') return '';
    // Always treat as string, pad with 0 if single digit
    const str = String(value);
    return str.length === 1 ? `0${str}` : str;
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const fetchContacts = async () => {
    try {
      const response = await axiosInstance.get(`/accounts/${id}/contacts`);
      const contactDetails = response.data.map(contact => ({
        name: `${contact.first_name} ${contact.last_name}`.trim(),
        email: contact.email, // Extract email
        mobile: contact.mobile_number, // Extract mobile
        details: contact, // Keep the whole contact object for details
        account_id: contact.account_id,
        account_name: contact.account_name,

      }));
      setContacts(contactDetails); // Update state with full contact details
      //console.log('Processed contact details:', contactDetails);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };


  const fetchActivities = async () => {
    if (!hasTasksModule) {
      setActivities([]);
      return;
    }
    try {
      const params = new URLSearchParams();
      if (true) {
        params.append('parent_table', 'accounts');
      }
      if (true) {
        params.append('parent_table_id', id);
      }

      // const response = await axiosInstance.get(`/activities/`, { params });
      const response = await axiosInstance.get('/activities/', {
        params: {
          parent_table: 'accounts',
          parent_table_id: id
        }
      });
      //console.log('Fetched activities:', response.data);
      setActivities(response.data || []);
    } catch (error) {
      if (!isModuleNotEntitledError(error)) {
        console.error(`Error fetching activities: ${error}`);
        setSnackbar({
          open: true,
          message: getApiErrorMessage(error, 'Error fetching activities'),
          severity: 'error',
        });
      }
      setActivities([]);
    }
  };


  const handleOpenActivityModal = () => {
    setIsActivityModalOpen(true);
  };

  const handleCloseActivityModal = () => {
    setIsActivityModalOpen(false);
    setNewActivity({
      account_id: id,
      type: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
  };

  const handleActivitySaved = async (newActivityData) => {
    setIsActivitiesLoading(true);
    try {
      const currentDate = new Date().toISOString();
      setActivities([...activities, newActivityData]);

      const lastActivity = await axiosInstance.patch(`/accounts/${id}`, {
        last_activity: currentDate,
      });

      //console.log('Last activity updated:', lastActivity.data);

      setSnackbar({
        open: true,
        message: 'Activity created successfully!',
        severity: 'success',
      });

      await fetchAccountData(); // Refresh lead data to get updated information
      // --- Log activity creation ---
      await frontendLogger.logCreate('Activity', {
        id: newActivityData.id,
        account_id: id,
        type: newActivityData.type,
        title: newActivityData.title,
        created_by: user.id,
        user: user.username,
        created_at: newActivityData.created_at || new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating last activity:', error);
      setSnackbar({
        open: true,
        message: 'Error updating last activity',
        severity: 'error',
      });
    } finally {
      setIsActivitiesLoading(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    setIsLoading(true);
    try {
      await axiosInstance.delete(`/activities/${activityId}`);
      setActivities(activities.filter((activity) => activity.id !== activityId));
      setSnackbar({
        open: true,
        message: 'Activity deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting activity:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Activity deletion',
        activityId: activityId,
        accountId: id,
        user: user?.username,
        action: 'delete',
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Error deleting activity',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditActivity = (updated) => {
    if (!updated?.id) return;
    setActivities((prev) =>
      prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
    );
  };


  const handleAgentClick = (agentId) => {
    if (agentId) {
      navigate(`/users/${agentId}/profile`);
    } else {
      setSnackbar({
        open: true,
        message: 'This account is not assigned to any agent.',
        severity: 'info',
      });
    }
  };

  const handleCopyContactInfo = async (type, value) => {
    const notify = (message, severity) =>
        setSnackbar({ open: true, message, severity, autoHideDuration: 3000 });

    if (!value) {
      notify(`No ${type} to copy`, 'warning');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      notify(`Successfully copied ${type}`, 'success');
    } catch (err) {
      // Fallback for non-HTTPS or older browsers
      try {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        notify(`Successfully copied ${type}`, 'success');
      } catch (e) {
        notify(`Failed to copy ${type}`, 'error');
      }
    }
  };

  const handleContactClick = (contact) => {
    // Navigate to contact detail page using the contact ID from contact.details
    navigate(`/contacts/${contact.details.id}`);
  };
  const ContactDetailsDialog = ({ open, onClose, contact }) => (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Contact Details</DialogTitle>
      <DialogContent>
        <Typography variant="body1">Name: {contact?.name}</Typography>
        <Typography variant="body2">Email: {contact?.details.email}</Typography>
        {/* Add more details as needed */}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );



  const handleEditToggle = () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to edit accounts.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    setIsEditLoading(true);
    try {
      if (isEditing) {
        handleSaveChanges();
      }
      setIsEditing(!isEditing);
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleFieldUpdate = (field, value) => {
    setUpdatedFields((prev) => {
      let updatedValue = value;

      // Handle custom fields (fields starting with cf_)
      if (field.startsWith('cf_')) {
        // Handle empty values
        if (value === '' || value === undefined) {
          updatedValue = null;
        }
        // Handle number fields - allow 0 values
        else if (typeof value === 'string' && !isNaN(value)) {
          const numValue = parseFloat(value);
          updatedValue = Number.isFinite(numValue) ? Number(numValue.toFixed(2)) : value;
        }
        // Handle boolean values
        else if (value === true || value === false) {
          updatedValue = value;
        }
        // Handle string values
        else if (value === null || typeof value === 'string') {
          updatedValue = value;
        }
      }

      // Preserve existing values for billing_stcd and shipping_stcd
      const updated = { ...prev, [field]: updatedValue };

      // Ensure required fields are included
      if (!updated.name && account.name) {
        updated.name = account.name;
      }

      // Preserve billing_stcd and shipping_stcd values
      if (field === 'billing_stcd' && !updated.shipping_stcd) {
        updated.shipping_stcd = prev.shipping_stcd; // Keep the previous shipping_stcd value
      } else if (field === 'shipping_stcd' && !updated.billing_stcd) {
        updated.billing_stcd = prev.billing_stcd; // Keep the previous billing_stcd value
      }

      return updated;
    });

    // Clear any errors for this field
    setFormErrors((prev) => {
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  };

  // Add handleFieldError function
  const handleFieldError = (field, error, value) => {
    setFormErrors(prev => {
      if (error) {
        return { ...prev, [field]: error };
      } else if (field.is_required && (value === null || value === undefined || value === '')) {
        return 'This field is required';
      } else {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
    });
  };

  const handleDiscardChanges = () => {
    setIsCancelLoading(true);
    setIsDetailsLoading(true); // Show skeleton for details only
    try {
      setIsEditing(false);
      setUpdatedFields({});
      fetchAccountData();
      fetchActivities();
      fetchAttachments();
    } finally {
      setIsCancelLoading(false);
      setIsDetailsLoading(false); // Stop skeleton loading
    }
  };

  const handleSaveChanges = async () => {
    setIsSaveLoading(true);
    setIsDetailsLoading(true); // Show skeleton for details only
    try {
      const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true;

      // Partner-specific validations on edit
      const effectiveType = updatedFields.account_type ?? account?.account_type;
      if (effectiveType === 'Partner') {
        const partnerErrors = {};
        const nameVal = (updatedFields.name ?? account?.name ?? '').toString().trim();
        const partnerTypeVal = (updatedFields.partner_type ?? account?.partner_type ?? '').toString().trim();
        const serviceTypeVal = updatedFields.service_type ?? account?.service_type ?? [];
        const countryVal = (updatedFields.country_of_registration ?? account?.country_of_registration ?? '').toString().trim();
        const emailVal = (updatedFields.email ?? account?.email ?? '').toString().trim();
        const phoneVal = (updatedFields.phone ?? account?.phone ?? '').toString().trim();
        const billingVal = (updatedFields.billing_address ?? account?.billing_address ?? '').toString().trim();
        const currencyVal = (updatedFields.currency ?? account?.currency ?? '').toString().trim();
        const websiteVal = (updatedFields.website ?? account?.website ?? '').toString().trim();
        const tradingVal = (updatedFields.trading_name ?? account?.trading_name ?? '').toString().trim();

        if (!nameVal) partnerErrors.name = 'Enter account name.';
        if (!partnerTypeVal) partnerErrors.partner_type = 'Select partner type.';
        if (partnerTypeVal === 'Merchant' && (!Array.isArray(serviceTypeVal) || serviceTypeVal.length === 0)) {
          partnerErrors.service_type = 'Select at least one service type.';
        }
        if (!countryVal) partnerErrors.country_of_registration = 'Select a country.';
        if (!emailVal) partnerErrors.email = 'Enter business email.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) partnerErrors.email = 'Enter a valid email.';
        if (!phoneVal) partnerErrors.phone = 'Enter business phone.';
        if (!billingVal) partnerErrors.billing_address = 'Enter billing address.';
        if (!currencyVal) partnerErrors.currency = 'Select currency.';
        const tradingTouched = Object.prototype.hasOwnProperty.call(updatedFields, 'trading_name');
        if (tradingTouched && !tradingVal) partnerErrors.trading_name = 'Enter trading name.';
        if (websiteVal && !/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(websiteVal)) {
          partnerErrors.website = 'Enter a valid website.';
        }

        if (Object.keys(partnerErrors).length > 0) {
          setFormErrors((prev) => ({ ...prev, ...partnerErrors }));
          setSnackbar({
            open: true,
            message: Object.values(partnerErrors)[0],
            severity: 'error',
          });
          return;
        }
      }

      // Check for validation errors and empty changes upfront
      if (Object.keys(formErrors).length > 0 || !isCustomFieldsValid) {
        setSnackbar({
          open: true,
          message: 'Please fill the required fields before saving.',
          severity: 'error',
        });
        return; // Early return for validation failure
      }

      if (Object.keys(updatedFields).length === 0) {
        setSnackbar({
          open: true,
          message: 'No changes to save.',
          severity: 'info',
        });
        setIsEditing(false);
        return; // Early return for no changes
      }

      // Prepare the payload
      const payload = {
        ...updatedFields,
        tax_id: updatedFields.tax_id ? updatedFields.tax_id.trim() : '', // Trim the Tax ID
        updated_at: new Date().toISOString(),
        custom_fields: {},
        billing_stcd:
          updatedFields.billing_stcd !== undefined
            ? (updatedFields.billing_stcd === '' ? null : updatedFields.billing_stcd)
            : account.billing_stcd,
        shipping_stcd:
          updatedFields.shipping_stcd !== undefined
            ? (updatedFields.shipping_stcd === '' ? null : updatedFields.shipping_stcd)
            : account.shipping_stcd,
      };

      const effectivePartnerType = (updatedFields.partner_type ?? account?.partner_type ?? '').toString().trim();
      if (effectiveType === 'Partner' && effectivePartnerType !== 'Merchant') {
        payload.service_type = [];
      }

      Object.keys(payload).forEach((key) => {
        if (key.startsWith('cf_')) {
          payload.custom_fields[key] = payload[key];
          delete payload[key];
        }
      });

      // Update the account
      await axiosInstance.patch(`/accounts/${account.id}`, payload);

      // Refresh account data
      const refreshResponse = await axiosInstance.get(`/accounts/${account.id}`);
      setAccount(refreshResponse.data);
      fetchPartnerOptions();

      // Log the account update with correct parameters
      await frontendLogger.logUpdate('Account', account.id, {
        action: 'edit',
        updated_fields: Object.keys(updatedFields),
        changes: updatedFields,
        user: user.username,
        updated_by: user.id,
        timestamp: new Date().toISOString(),
        account_name: account.name,
        account_type: account.account_type,
        account_status: account.account_status
      });

      // Reset states and notify success
      setUpdatedFields({});
      setFormErrors({});
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'Account updated successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating account:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Account update',
        accountId: account.id,
        updatedFields: Object.keys(updatedFields),
        user: user?.username,
        action: 'edit',
        timestamp: new Date().toISOString(),
        account_name: account.name,
        account_type: account.account_type,
        account_status: account.account_status
      });

      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Error updating account. Please try again.'),
        severity: 'error',
      });
    } finally {
      setIsSaveLoading(false); // Always reset loading state
      setIsDetailsLoading(false); // Stop skeleton loading
    }
  };
  // Add this new function to check if name exists
  const checkNameExists = async (name, { excludeId = id } = {}) => {
    try {
      const response = await axiosInstance.get('/accounts/', {
        params: {
          name: name.trim() // Trim whitespace from name
        }
      });
      // Check if any account has exactly the same name (case-insensitive).
      // Clone must not exclude the source account — same name would create a duplicate.
      return response.data.some((acct) =>
        acct.name.toLowerCase() === name.trim().toLowerCase() &&
        (excludeId == null || acct.id !== excludeId)
      );
    } catch (error) {
      console.error('Error checking account name:', error);
      throw error;
    }
  };


  const handleCloneClick = () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to clone accounts.',
        severity: 'error',
      });
      return;
    }
    if (!account) {
      setSnackbar({
        open: true,
        message: 'Cannot clone account: Account data not loaded',
        severity: 'error',
      });
      return;
    }
    setNameError('');
    setIsCloneModalOpen(true);
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axiosInstance.delete(`/attachments/${attachmentId}`);
      setAttachments(attachments.filter((attachment) => attachment.id !== attachmentId));
      setSnackbar({
        open: true,
        message: 'Attachment deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Attachment deletion',
        attachmentId: attachmentId,
        accountId: id,
        user: user?.username,
        action: 'delete',
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Error deleting attachment',
        severity: 'error',
      });
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment.bucket_name || !attachment.storage_path) {
        throw new Error('Attachment storage metadata is invalid for strict R2 download');
      }
      await downloadPrivateObject({
        axiosInstance,
        attachmentId: attachment.id,
        fileName: attachment.file_name
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading attachment',
        severity: 'error',
      });
    }
  };
  const fetchAttachments = async () => {
    const parentTable = 'accounts';
    const parentId = id;
    try {
      const response = await axiosInstance.get('/attachments/', {
        params: {
          parent_table: parentTable,
          parent_id: parentId
          // skip: 0,
          // limit: 100,
          // sort_by: 'created_at',
          // sort_order: 'desc'
        }
      });

      setAttachments(response.data || []);

    } catch (error) {
      console.error('Error fetching attachments:', error);
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Error fetching attachments'),
        severity: 'error',
        autoHideDuration: 5000
      });
    } finally {
      // If you have a loading state
      // setLoading(false);
    }
  };
  const [deleteDependencies, setDeleteDependencies] = useState(null);
  const [isCheckingDependencies, setIsCheckingDependencies] = useState(false);

  const checkAccountDependencies = async () => {
    setIsCheckingDependencies(true);
    try {
      const response = await axiosInstance.get(`/accounts/${id}/dependencies`);
      setDeleteDependencies(response.data);
    } catch (error) {
      console.error('Error checking dependencies:', error);
      setDeleteDependencies(null);
    } finally {
      setIsCheckingDependencies(false);
    }
  };

  const handleOpenDeleteModal = async () => {
    setIsDeleteLoading(true);
    try {
      // Check dependencies before opening the modal
      await checkAccountDependencies();
      setIsDeleteModalOpen(true);
    } catch (error) {
      console.error('Error checking dependencies:', error);
      // Still open the modal even if dependency check fails
      setIsDeleteModalOpen(true);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);



  const handleUploadSuccess = async (uploadData) => {
    //console.log('Upload successful:', uploadData);

    const uploadDataPayload = {
      file_name: uploadData.file.name,
      file_type: uploadData.file.type,
      file_size: uploadData.file.size,
      bucket_name: uploadData.bucket,
      storage_path: uploadData.path,
      description: uploadData.description || '', // Optional, use default if no description is provided
      parent_table: 'accounts',  // Assuming the parent table is 'leads', modify as needed
      parent_id: id,  // Assuming the parent ID is the same as lead ID
      is_active: true,
      is_private: uploadData.isPrivate || false, // Default to false if not provided
      file_url: uploadData.url,
      signed_url_expiry: uploadData.signedUrlExpiry || null,
    };

    setIsAttachmentModalOpen(false);
    try {
      const response = await axiosInstance.post(`/attachments/`, uploadDataPayload);

      setSnackbar({
        open: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      });

      // Update files or attachments state as needed
      // setFiles([...files, response.data]); // Uncomment if `files` state exists
      // --- Log attachment creation ---
      await frontendLogger.logCreate('Attachment', {
        id: response.data.id,
        account_id: id,
        file_name: uploadData.file.name,
        file_type: uploadData.file.type,
        file_size: uploadData.file.size,
        user: user.username,
        uploaded_by: user.id,
        uploaded_at: response.data.created_at || new Date().toISOString()
      });
    } catch (error) {
      console.error('Error uploading file:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'File upload',
        accountId: id,
        fileName: uploadData.file.name,
        user: user?.username,
        action: 'upload',
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Error uploading file',
        severity: 'error',
      });
    } finally {
      fetchAttachments();
    }
  };



  const handleCloneSave = async (formDataToSave) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to clone accounts.',
        severity: 'error',
      });
      throw new Error('Support users cannot clone accounts');
    }
    try {
      setIsCloneButtonLoading(true);
      setIsCheckingName(true);

      const nameExists = await checkNameExists(formDataToSave.name, { excludeId: null });
      if (nameExists) {
        const msg = 'An account with this name already exists. Please choose a different name.';
        setNameError(msg);
        const nameErr = new Error(msg);
        nameErr.response = { data: { detail: msg } };
        throw nameErr;
      }

      const clonedAccountData = {
        ...formDataToSave,
        created_by: user.id,
        updated_by: user.id,
        created_by_name: user.username,
        updated_by_name: user.username,
        custom_fields: formDataToSave.custom_fields || {},
      };

      // Flatten cf* keys into custom_fields when the form put them on the root.
      Object.keys(clonedAccountData).forEach((key) => {
        if (key.startsWith('cf_') || /^cf\d+$/.test(key)) {
          clonedAccountData.custom_fields[key] = clonedAccountData[key];
        }
      });

      const response = await axiosInstance.post('/accounts/', clonedAccountData);

      setSnackbar({
        open: true,
        message: 'Account cloned successfully!',
        severity: 'success',
      });

      await frontendLogger.logCreate('Account', {
        id: response.data.id,
        action: 'clone',
        cloned_from: account.id,
        user: user.username,
        timestamp: new Date().toISOString(),
      });

      setIsCloneModalOpen(false);

      setTimeout(() => {
        navigate(`/accounts/${response.data.id}`);
      }, 1000);

      return response.data;
    } catch (error) {
      console.error('Error cloning account:', error);

      await frontendLogger.logError(error, {
        context: 'Account cloning',
        originalAccountId: account.id,
        newAccountName: formDataToSave?.name,
        user: user?.username,
        action: 'clone',
        timestamp: new Date().toISOString(),
      });

      // Re-throw so AccountFormModal can highlight email/name field errors.
      throw error;
    } finally {
      setIsCloneButtonLoading(false);
      setIsCheckingName(false);
      setIsCloning(false);
    }
  };



  const handleDelete = async () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to delete accounts.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    try {
      await axiosInstance.delete(`/accounts/${account.id}`);

      // Log the account deletion with correct parameters
      await frontendLogger.logDelete('Account', {
        id: account.id,
        name: account.name,
        user: user.username,
        deleted_by: user.id
      });

      navigate('/accounts/');
    } catch (error) {
      console.error('Error deleting account:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Account deletion',
        accountId: account.id,
        accountName: account.name,
        user: user?.username,
        action: 'delete',
        timestamp: new Date().toISOString(),
      });

      // Handle different types of errors with better user messages
      let errorMessage = 'Error deleting account. Please try again.';
      let severity = 'error';

      if (error.response) {
        const { status, data } = error.response;

        if (status === 400 && data?.detail) {
          // This is our custom dependency check error message
          errorMessage = data.detail;
          severity = 'warning'; // Use warning for dependency issues
        } else if (status === 403) {
          errorMessage = 'You do not have permission to delete this account.';
        } else if (status === 404) {
          errorMessage = 'Account not found or already deleted.';
        } else if (status === 500) {
          errorMessage = 'Server error occurred while deleting account. Please try again.';
        }
      } else if (error.request) {
        errorMessage = 'No response received from server. Please check your connection.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: severity,
        autoHideDuration: 8000, // Show longer for dependency messages
      });
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleSaveOrder = async (data) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create orders.',
        severity: 'error',
      });
      return;
    }
    // Generate a unique order name
    const uniqueOrderName = `ORD-${Date.now()}`; // You can also use a UUID if preferred

    // Prepare the tax slabs and amounts from the tax summary
    const taxSummary = data.tax_summary || [];
    const taxSlabs = taxSummary.map(item => item.taxRate); // Extract tax rates (e.g., [5, 10, 18])
    const taxAmounts = taxSummary.map(item => item.amount); // Extract tax amounts (e.g., [1223, 456, 789])

    // Prepare the main order data with tax slabs and amounts
    const orderData = {
      account_id: account.id, // Use the account ID from the account object
      account_name: account.name,
      quotation_id: data.quotation_id,
      contact_id: data.contact_id,
      deal_name: data.deal_name, // Include deal name
      // order_number: uniqueOrderName, // Set the unique order name
      total_amount: data.total_amount,
      grand_total: data.total_amount,
      status: data.status,
      notes: data.notes,
      shipping_method: data.shipping_method,
      shipping_instruction: data.shipping_instruction,
      // payment_methods: data.payment_methods,
      // order_date: data.order_date,
      tax: data.tax,
      delivery_date: data.delivery_date || null,
      subtotal: data.subtotal, // Include subtotal
      // Add tax slabs and amounts directly into the order data
      tax_slab_1: data.tax_slab_1 || taxSlabs[0] || null,
      tax_slab_2: data.tax_slab_2 || taxSlabs[1] || null,
      tax_slab_3: data.tax_slab_3 || taxSlabs[2] || null,
      tax_slab_4: data.tax_slab_4 || taxSlabs[3] || null,
      tax_slab_5: data.tax_slab_5 || taxSlabs[4] || null,
      tax_slab_amount_1: data.tax_slab_amount_1 || taxAmounts[0] || null,
      tax_slab_amount_2: data.tax_slab_amount_2 || taxAmounts[1] || null,
      tax_slab_amount_3: data.tax_slab_amount_3 || taxAmounts[2] || null,
      tax_slab_amount_4: data.tax_slab_amount_4 || taxAmounts[3] || null,
      tax_slab_amount_5: data.tax_slab_amount_5 || taxAmounts[4] || null,
      created_by: user?.id,
      updated_by: user?.id,
      created_by_name: user?.fullname,
      updated_by_name: user?.fullname,
    };

    // console.log('New Order Data:', orderData); // Log the order data being saved

    // Check for missing fields
    const missingFields = [];
    for (const [key, value] of Object.entries(orderData)) {
      if (value === null || value === undefined) {
        missingFields.push(key);
      }
    }
    if (missingFields.length > 0) {
      console.warn('Missing fields in order data:', missingFields);
    }

    setIsLoading(true);
    handleCloseOrderModal();

    try {
      // Create the order
      const orderResponse = await axiosInstance.post('/orders/', orderData);
      const newOrderId = orderResponse.data.id;

      // console.log('Order created with ID:', newOrderId); // Log the new order ID

      // Add order_id to each item
      const orderItems = data.items.map((item) => ({
        order_id: newOrderId, // Attach the order_id
        product_name: item.product_name,
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: parseFloat(item.price), // Ensure numerical fields are properly formatted
        total_price: parseFloat(item.total_price),
        discount_percentage: item.discount_percentage || null,
        tax: item.tax || null,
        discounted_price: item.discounted_price || null,
      }));


      // console.log('Order Items before saving:', orderItems); // Log the order items being saved

      // Create the order items
      await Promise.all(
        orderItems.map((item) =>
          axiosInstance.post('/orders/items/', item)
        )
      );
      // Fetch the updated order with items
      const updatedOrderResponse = await axiosInstance.get(`/orders/${newOrderId}`);

      setOrders((prevOrders) => [...prevOrders, updatedOrderResponse.data]);
      // --- Log order creation ---
      await frontendLogger.logCreate('Order', {
        id: newOrderId,
        account_id: account.id,
        account_name: account.name,
        total_amount: orderData.total_amount,
        status: orderData.status,
        created_by: user.id,
        user: user.username,
        created_at: orderResponse.data.created_at || new Date().toISOString()
      });
      setSnackbar({
        open: true,
        message: 'Order created successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error saving order:', error);
      if (error.response) {
        console.error('Response data:', error.response.data); // Log the response data for more details
        console.error('Response status:', error.response.status); // Log the response status
      }

      // Log error
      await frontendLogger.logError(error, {
        context: 'Order creation',
        accountId: account.id,
        orderData: orderData,
        user: user?.username,
        action: 'create',
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: `Error saving order: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };


  const handleOpenOrderModal = () => {
    if (!showLegacyOrders) return;
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create orders.',
        severity: 'error',
      });
      return;
    }
    setIsOrderModalOpen(true);
    setTimeout(() => {
      setIsOrderModalOpen(true);
      setIsOrderLoading(false);
    }, 300);
  };

  const handleCloseOrderModal = () => {
    setIsOrderModalOpen(false);
    setNewOrder({
      // deal_id: id,
      total_amount: '',
    })
  }

  const handleOpenContactModal = () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create contacts.',
        severity: 'error',
      });
      return;
    }
    setIsContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false);
  };


  const [attachments, setAttachments] = useState([]);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);


  const formatDateTime = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  // Deal Card Component
  const DealCard = ({ deal, onClick }) => {
    // Color mapping for different deal stages
    const stageColorMap = {
      'Qualification': 'info',
      'Needs Analysis': 'warning',
      'Proposal': 'primary',
      'Negotiation': 'secondary',
      'Closed Won': 'success',
      'Closed Lost': 'error'
    };

    // Format currency
    // const formatCurrency = (amount) => {
    //   return new Intl.NumberFormat('en-IN', {
    //     style: 'currency',
    //     currency: 'INR'
    //   }).format(amount || 0);
    // };

    // Format date
    const formatDealDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(date);
    };

    return (
      <Box
        onClick={() => onClick && onClick(deal)}
        sx={{
          mb: 1,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'rgba(0,0,0,0.08)',
          overflow: 'hidden',
          '&:hover': {
            transform: onClick ? 'translateY(-2px)' : 'none',
            boxShadow: onClick ? '0 4px 10px rgba(0,0,0,0.15)' : 'none',
            borderColor: 'rgba(0,0,0,0.12)',
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: deal.stage ? '1px solid rgba(0,0,0,0.05)' : 'none',
        }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight="600"
              sx={{
                color: '#344767',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                lineHeight: 1.35,
              }}
            >
              {deal.name || 'Unnamed Deal'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight="medium" sx={{ mr: 1.5, color: '#344767' }}>
              {formatCurrency(deal.value, deal.currency)}
            </Typography>
            <Chip
              label={deal.stage || 'No Stage'}
              color={stageColorMap[deal.stage] || 'default'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                '.MuiChip-label': { px: 1 },
                color: '#ffffff'
              }}
            />
          </Box>
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.75,
          bgcolor: 'rgba(0,0,0,0.02)'
        }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Est. Closure: {formatDealDate(deal.estimated_closure_date) || '-'}
          </Typography>

          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Probability: {deal.probability || 0}%
          </Typography>
        </Box>
      </Box>
    );
  };

  // Deals List component - a more space-efficient design
  const DealsList = ({ deals, onDealClick }) => {
    // For empty state
    if (!deals || deals.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
            px: 2,
          }}
        >
          <Box sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.04)',
            mb: 1.5
          }}>
            <LocalOfferIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center">
            No deals to display.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
            Create your first deal by clicking the "Add Deal" button above.
          </Typography>
        </Box>
      );
    }

    // Dynamic height based on number of deals
    const containerHeight = Math.min(250, deals.length * 80);

    return (
      <Box
        sx={{
          maxHeight: deals.length > 2 ? containerHeight : 'auto',
          overflowY: deals.length > 2 ? 'auto' : 'visible',
          pb: 0.5,
          pt: 2,
          px: 1.5,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
        }}
      >
        {deals.map(deal => (
          <DealCard
            key={deal.id}
            deal={deal}
            onClick={onDealClick}
          />
        ))}
      </Box>
    );
  };

  const handleDealClick = (deal) => {
    // Navigate to deal details or open a modal
    navigate(`/deals/${deal.id}`);
    // or
    // setSelectedDeal(deal);
    // setIsDealDetailModalOpen(true);
  };


  const fetchOrders = async () => {
    try {
      const response = await axiosInstance.get(`/orders/account/${id}`);
      setOrders(response.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchCases = async () => {
    try {
      const response = await axiosInstance.get(`/cases/account/${id}`);
      setCases(response.data || []);
    } catch (error) {
      console.error('Error fetching cases:', error);
    }
  };

  const fetchMerchantAssets = async () => {
    if (!id) return;
    setIsMerchantAssetsLoading(true);
    try {
      const response = await axiosInstance.get('/merchant-assets/', {
        params: { merchant_id: id, limit: 100 },
      });
      setMerchantAssets(response.data || []);
    } catch (error) {
      console.error('Error fetching merchant assets:', error);
      setMerchantAssets([]);
    } finally {
      setIsMerchantAssetsLoading(false);
    }
  };

  const fetchMerchantAllocations = async () => {
    if (!id) return;
    setIsMerchantAllocationsLoading(true);
    try {
      const response = await axiosInstance.get('/merchant-allocations/', {
        params: { merchant_id: id, limit: 100 },
      });
      setMerchantAllocations(response.data || []);
    } catch (error) {
      console.error('Error fetching merchant allocations:', error);
      setMerchantAllocations([]);
    } finally {
      setIsMerchantAllocationsLoading(false);
    }
  };

  const handleSaveMerchantAsset = async (payload) => {
    await axiosInstance.post('/merchant-assets/', payload);
    enqueueSnackbar('Merchant Asset created successfully', { variant: 'success' });
    await fetchMerchantAssets();
  };

  const handleSaveMerchantAllocation = async (payload) => {
    await axiosInstance.post('/merchant-allocations/', payload);
    enqueueSnackbar('Merchant Allocation created successfully', { variant: 'success' });
    await fetchMerchantAllocations();
  };

  const MerchantAssetCard = ({ asset, onClick }) => (
    <Box
      onClick={() => onClick && onClick(asset)}
      sx={{
        mb: 1,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        overflow: 'hidden',
        '&:hover': onClick ? {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderColor: 'rgba(0,0,0,0.12)',
        } : {},
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight="medium" sx={{ color: 'info.main' }}>
            {asset.name}
          </Typography>
          <Chip
            label={asset.active ? 'Active' : 'Inactive'}
            size="small"
            color={asset.active ? 'success' : 'default'}
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {asset.asset_type || '-'}
          {asset.geo_location ? ` · ${asset.geo_location}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Capacity/Unit: {asset.capacity_per_slot != null ? asset.capacity_per_slot : '-'}
        </Typography>
      </Box>
    </Box>
  );

  const MerchantAssetsList = ({ assets, onAssetClick, loading }) => {
    if (loading) {
      return (
        <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!assets || assets.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
            px: 2,
          }}
        >
          <Box sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.04)',
            mb: 1.5
          }}>
            <InventoryIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center">
            No merchant assets configured yet.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
            Create a Merchant Asset for this Merchant using the New button above.
          </Typography>
        </Box>
      );
    }

    const containerHeight = Math.min(250, assets.length * 90);

    return (
      <Box
        sx={{
          maxHeight: assets.length > 2 ? containerHeight : 'auto',
          overflowY: assets.length > 2 ? 'auto' : 'visible',
          pb: 0.5,
          pt: 2,
          px: 1.5,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '10px' },
        }}
      >
        {assets.map((asset) => (
          <MerchantAssetCard
            key={asset.id}
            asset={asset}
            onClick={onAssetClick}
          />
        ))}
      </Box>
    );
  };

  const MerchantAllocationCard = ({ allocation, onClick }) => (
    <Box
      onClick={() => onClick && onClick(allocation)}
      sx={{
        mb: 1,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        overflow: 'hidden',
        '&:hover': onClick ? {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderColor: 'rgba(0,0,0,0.12)',
        } : {},
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight="medium" sx={{ color: 'info.main' }}>
            {allocation.name}
          </Typography>
          <Chip
            label={allocation.status || (allocation.is_active === false ? 'Inactive' : 'Pending')}
            size="small"
            color={
              String(allocation.status || '').toLowerCase() === 'accepted'
                ? 'success'
                : String(allocation.status || '').toLowerCase() === 'rejected'
                  ? 'error'
                  : 'default'
            }
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {allocation.pricing_model || '-'}
          {allocation.currency ? ` · ${allocation.currency}` : ''}
        </Typography>
        {allocation.min_number != null && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Requested: {allocation.min_number}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const MerchantAllocationsList = ({ allocations, onAllocationClick, loading }) => {
    if (loading) {
      return (
        <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!allocations || allocations.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
            px: 2,
          }}
        >
          <Box sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.04)',
            mb: 1.5
          }}>
            <AssignmentIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center">
            No merchant allocations configured yet.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
            Create a Merchant Allocation for this Merchant using the New button above.
          </Typography>
        </Box>
      );
    }

    const containerHeight = Math.min(250, allocations.length * 90);

    return (
      <Box
        sx={{
          maxHeight: allocations.length > 2 ? containerHeight : 'auto',
          overflowY: allocations.length > 2 ? 'auto' : 'visible',
          pb: 0.5,
          pt: 2,
          px: 1.5,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '10px' },
        }}
      >
        {allocations.map((allocation) => (
          <MerchantAllocationCard
            key={allocation.id}
            allocation={allocation}
            onClick={onAllocationClick}
          />
        ))}
      </Box>
    );
  };

 

  const handleSaveCase = async (caseData) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create cases.',
        severity: 'error',
      });
      return;
    }
    try {
      const response = await axiosInstance.post('/cases/', caseData);

      // Log the case creation with correct parameters
      await frontendLogger.logCreate('Case', {
        id: response.data.id,
        account_id: id,
        subject: caseData.subject,
        priority: caseData.priority,
        status: caseData.status,
        created_by: user.id,
        user: user.username,
        created_at: response.data.created_at || new Date().toISOString()
      });

      enqueueSnackbar('Case created successfully!', { variant: 'success' });
      fetchCases();
      setIsCaseModalOpen(false);
    } catch (error) {
      console.error('Error creating case:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Case creation',
        accountId: id,
        caseData: caseData,
        user: user?.username,
        action: 'create',
        timestamp: new Date().toISOString(),
      });

      enqueueSnackbar('Error creating case', { variant: 'error' });
    }
  };

  const CaseCard = ({ case: caseItem, onClick }) => {
    // Color mapping for different case statuses
    const statusColorMap = {
      'Open': 'info',
      'In Progress': 'warning',
      'Pending': 'primary',
      'Closed': 'success',
    };

    // Format date
    const formatCaseDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(date);
    };

    // Get truncated subject - limit to 3 words
    const getTruncatedSubject = (subject) => {
      if (!subject) return '';
      const words = subject.split(' ');
      if (words.length <= 3) return subject;
      return `${words.slice(0, 3).join(' ')}...`;
    };

    return (
      <Box
        onClick={() => onClick && onClick(caseItem)}
        sx={{
          mb: 1,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'rgba(0,0,0,0.08)',
          overflow: 'hidden',
          '&:hover': {
            transform: onClick ? 'translateY(-2px)' : 'none',
            boxShadow: onClick ? '0 4px 10px rgba(0,0,0,0.15)' : 'none',
            borderColor: 'rgba(0,0,0,0.12)',
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: caseItem.status ? '1px solid rgba(0,0,0,0.05)' : 'none',
        }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight="600" sx={{ mr: 1, color: '#344767', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
                {caseItem.case_number || 'Unnamed Case'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
                {getTruncatedSubject(caseItem.subject)}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={caseItem.status || 'No Status'}
            color={statusColorMap[caseItem.status] || 'default'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              '.MuiChip-label': { px: 1 },
              color: '#ffffff'
            }}
          />
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.75,
          bgcolor: 'rgba(0,0,0,0.02)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {caseItem.assigned_to_name || 'Unassigned'}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Updated: {formatCaseDate(caseItem.updated_at || caseItem.created_at)}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Cases List component - a more space-efficient design
  const CasesList = ({ cases, onCaseClick }) => {
    // For empty state
    if (!cases || cases.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
            px: 2,
          }}
        >
          <Box sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.04)',
            mb: 1.5
          }}>
            <SupportAgentIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center">
            No cases to display.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
            Create your first case by clicking the "Add Case" button above.
          </Typography>
        </Box>
      );
    }

    // Dynamic height based on number of cases
    const containerHeight = Math.min(250, cases.length * 80);

    return (
      <Box
        sx={{
          maxHeight: cases.length > 2 ? containerHeight : 'auto',
          overflowY: cases.length > 2 ? 'auto' : 'visible',
          pb: 0.5,
          pt: 2,
          px: 1.5,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
        }}
      >
        {cases.map(caseItem => (
          <CaseCard
            key={caseItem.id}
            case={caseItem}
            onClick={onCaseClick}
          />
        ))}
      </Box>
    );
  };

  const handleOpenCaseModal = () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create cases.',
        severity: 'error',
      });
      return;
    }
    setIsCaseLoading(true);
    setTimeout(() => {
      setIsCaseModalOpen(true);
      setIsCaseLoading(false);
    }, 300);
  };

  const handleCloseCaseModal = () => {
    setIsCaseModalOpen(false);
  };


  const OrderCard = ({ order, onClick }) => {
    // Color mapping for different order statuses
    const statusColorMap = {
      'Draft': 'info',
      'In Progress': 'warning',
      'Delivered': 'primary',
      'Cancelled': 'error',
      'Completed': 'success',
    };

    // Format currency
    // const formatCurrency = (amount) => {
    //   return new Intl.NumberFormat('en-IN', {
    //     style: 'currency',
    //     currency: 'INR'
    //   }).format(amount || 0);
    // };

    // Format date
    const formatOrderDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(date);
    };

    return (
      <Box
        onClick={() => onClick && onClick(order)}
        sx={{
          mb: 1,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'rgba(0,0,0,0.08)',
          overflow: 'hidden',
          '&:hover': {
            transform: onClick ? 'translateY(-2px)' : 'none',
            boxShadow: onClick ? '0 4px 10px rgba(0,0,0,0.15)' : 'none',
            borderColor: 'rgba(0,0,0,0.12)',
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: order.status ? '1px solid rgba(0,0,0,0.05)' : 'none',
        }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight="600" sx={{ mr: 1, color: '#344767', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
                {order.order_number || 'Unnamed Order'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
                {formatOrderDate(order.created_at)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight="medium" sx={{ mr: 1.5, color: '#344767' }}>
              {formatCurrency(order.total_amount, order.currency)}
            </Typography>
            <Chip
              label={order.status || 'No Status'}
              color={statusColorMap[order.status] || 'default'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                '.MuiChip-label': { px: 1 },
                color: '#ffffff'
              }}
            />
          </Box>
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.75,
          bgcolor: 'rgba(0,0,0,0.02)'
        }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Updated: {formatOrderDate(order.updated_at) || formatOrderDate(order.created_at)}
          </Typography>

          {/* Show items count if available */}
          {order.item_count > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {order.item_count} {order.item_count === 1 ? 'item' : 'items'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  // Orders List component - a more space-efficient design
  const OrdersList = ({ orders, onOrderClick }) => {
    // For empty state
    if (!orders || orders.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
            px: 2,
          }}
        >
          <Box sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.04)',
            mb: 1.5
          }}>
            <ShoppingCartIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center">
            No orders to display.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
            Create your first order by clicking the "Add Order" button above.
          </Typography>
        </Box>
      );
    }

    // Dynamic height based on number of orders
    const containerHeight = Math.min(250, orders.length * 80);

    return (
      <Box
        sx={{
          maxHeight: orders.length > 2 ? containerHeight : 'auto',
          overflowY: orders.length > 2 ? 'auto' : 'visible',
          pb: 0.5,
          pt: 2,
          px: 1.5,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
        }}
      >
        {orders.map(order => (
          <OrderCard
            key={order.id}
            order={order}
            onClick={onOrderClick}
          />
        ))}
      </Box>
    );
  };



  const renderSection = (title, fields, renderContent = null) => {
    // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
    const sectionBody = (
      <>
      <Grid container spacing={3}>
        {fields.map((item) => {
          let displayValue = account?.[item.field] ?? '';
          const effectiveTypeIsPartner =
              (updatedFields.account_type ?? account?.account_type) === 'Partner';

          // Address — autocomplete in edit mode (billing and shipping)
          if (
              (item.field === 'billing_address' || item.field === 'shipping_address') &&
              isEditing
          ) {
            return (
                <Grid item xs={12} md={6} key={item.field}>
                  <Box
                      sx={{
                        mt: 2.5, // lines the top edge up with City/State/Country (margin="normal")
                        '& .MuiOutlinedInput-root': {
                          minHeight: 60,
                          borderRadius: '12px',
                          alignItems: 'center',
                        },
                      }}
                  >
                    <AddressAutocompleteInput
                        label={item.label}
                        placeholder="Enter address"
                        value={updatedFields[item.field] ?? account?.[item.field] ?? ''}
                        onChange={(nextAddress) => handleFieldUpdate(item.field, nextAddress)}
                        error={Boolean(formErrors[item.field])}
                        errorMessage={formErrors[item.field]}
                        required={item.field === 'billing_address' && effectiveTypeIsPartner}
                    />
                  </Box>
                </Grid>
            );
          }

          // Handle formatted display for specific fields
          if (item.field === 'created_at' || item.field === 'updated_at') {
            displayValue = formatDate(displayValue, 'datetime');
          }

          // if (item.field === 'annual_revenue' && !isEditing) {
          //   displayValue = account?.[item.field] && account[item.field] !== 0
          //     ? new Intl.NumberFormat('en-IN', {
          //       style: 'currency',
          //       currency: 'INR'
          //     }).format(account[item.field])
          //     : ''; // Return empty string if value is 0 or undefined
          // }
          if (item.field === 'annual_revenue' && !isEditing) {
            displayValue = account?.[item.field] && account[item.field] !== 0
              ? formatCurrency(account[item.field], account?.currency || systemCurrency)
              : ''; // Return empty string if value is 0 or undefined
          }

          // Special handling for currency field
          // if (item.field === 'currency') {
          //   const currentCurrency = currencies.find(c => c.code === (updatedFields[item.field] || account?.[item.field])) || defaultCurrency;
          //   displayValue = isEditing ? currentCurrency?.code : `${currentCurrency?.name} ${currentCurrency?.code}`;

          //   if (isEditing) {
          //     return (
          //       <Grid item xs={12} md={6} key={item.field}>
          //         <Autocomplete
          //           options={currencies}
          //           getOptionLabel={(option) => `${option.name} ${option.code}`}
          //           renderInput={(params) => (
          //             <TextField
          //               {...params}
          //               label={item.label}
          //               variant="outlined"
          //               // margin="normal"
          //               fullWidth
          //               inputProps={{
          //                 ...params.inputProps,
          //                 style: { height: '10px' },
          //               }}
          //             />
          //           )}
          //           value={currencies.find(curr => curr.code === (currentCurrency?.code || defaultCurrency?.code)) || null}
          //           onChange={(_, newValue) => handleFieldUpdate(item.field, newValue ? newValue.code : '')}
          //           isOptionEqualToValue={(option, value) => option.code === value.code}
          //         />
          //       </Grid>
          //     );
          //   }
          // }
          if (item.field === 'currency') {
            const currentCurrencyCode = updatedFields[item.field] || account?.[item.field] || systemCurrency;
            const currentCurrency = currencies.find(c => c.code === currentCurrencyCode);
            displayValue = isEditing ? currentCurrencyCode : getCurrencyLabel(currentCurrencyCode);

            if (isEditing) {
              return (
                <Grid item xs={12} md={6} key={item.field}>
                  <Autocomplete
                    options={currencies}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={item.label}
                        variant="outlined"
                        fullWidth
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          shrink: true,
                        }}
                      />
                    )}
                    value={currentCurrency || null}
                    onChange={(_, newValue) => handleFieldUpdate(item.field, newValue ? newValue.code : '')}
                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                  />
                </Grid>
              );
            }
          }


          // Modified onSave handler to validate the `name` field
          const handleSave = (newValue) => {
            if (item.field === 'name' && newValue.trim() === '') {
              alert('Name field cannot be empty');
              return false; // Prevent save action
            }
            handleFieldUpdate(item.field, newValue);
            return true; // Allow save action
          };

          // Country / State / City — same geography selects as Travel destination form
          if (
            (item.field === 'billing_country' ||
              item.field === 'shipping_country' ||
              item.field === 'country_of_registration') &&
            isEditing
          ) {
            // Never invent a default country (e.g. India) when the field is blank.
            const rawCountry = updatedFields[item.field] ?? account?.[item.field];
            const currentCountry =
              rawCountry == null || String(rawCountry).trim() === ''
                ? ''
                : String(rawCountry).trim();
            const clearRelatedAddress = (countryField) => {
              if (countryField === 'billing_country') {
                setUpdatedFields((prev) => ({
                  ...prev,
                  billing_country: '',
                  billing_state: '',
                  billing_city: '',
                }));
              } else if (countryField === 'shipping_country') {
                setUpdatedFields((prev) => ({
                  ...prev,
                  shipping_country: '',
                  shipping_state: '',
                  shipping_city: '',
                }));
              } else {
                handleFieldUpdate(item.field, '');
              }
            };
            return (
              <Grid item xs={12} md={6} key={item.field}>
                <CountrySelect
                  label={item.label}
                  value={currentCountry}
                  margin="normal"
                  onChange={(nextCountry) => {
                    if (!nextCountry) {
                      clearRelatedAddress(item.field);
                      return;
                    }
                    if (item.field === 'billing_country') {
                      setUpdatedFields((prev) => ({
                        ...prev,
                        billing_country: nextCountry,
                        billing_state: '',
                        billing_city: '',
                      }));
                    } else if (item.field === 'shipping_country') {
                      setUpdatedFields((prev) => ({
                        ...prev,
                        shipping_country: nextCountry,
                        shipping_state: '',
                        shipping_city: '',
                      }));
                    } else {
                      handleFieldUpdate(item.field, nextCountry);
                    }
                  }}
                />
              </Grid>
            );
          }

          if (
            (item.field === 'billing_state' || item.field === 'shipping_state') &&
            isEditing
          ) {
            const isBilling = item.field === 'billing_state';
            const countryField = isBilling ? 'billing_country' : 'shipping_country';
            const cityField = isBilling ? 'billing_city' : 'shipping_city';
            const currentCountry =
              updatedFields[countryField] ?? account?.[countryField] ?? '';
            const currentState =
              updatedFields[item.field] ?? account?.[item.field] ?? '';
            return (
              <Grid item xs={12} md={6} key={item.field}>
                <StateSelect
                  label={item.label}
                  country={currentCountry}
                  value={currentState}
                  margin="normal"
                  onChange={(nextState) => {
                    setUpdatedFields((prev) => ({
                      ...prev,
                      [item.field]: nextState || '',
                      [cityField]: '',
                    }));
                  }}
                />
              </Grid>
            );
          }

          if (
            (item.field === 'billing_city' || item.field === 'shipping_city') &&
            isEditing
          ) {
            const isBilling = item.field === 'billing_city';
            const countryField = isBilling ? 'billing_country' : 'shipping_country';
            const stateField = isBilling ? 'billing_state' : 'shipping_state';
            const currentCountry =
              updatedFields[countryField] ?? account?.[countryField] ?? '';
            const currentState =
              updatedFields[stateField] ?? account?.[stateField] ?? '';
            const currentCity =
              updatedFields[item.field] ?? account?.[item.field] ?? '';
            return (
              <Grid item xs={12} md={6} key={item.field}>
                <CitySelect
                  label={item.label}
                  country={currentCountry}
                  state={currentState}
                  value={currentCity}
                  margin="normal"
                  onChange={(nextCity) => handleFieldUpdate(item.field, nextCity || '')}
                />
              </Grid>
            );
          }

          // Configurable Partner Type (freeSolo like product category)
          if (item.field === 'partner_type' && isEditing) {
            const currentPartnerType = updatedFields.partner_type ?? account?.partner_type ?? '';
            const updatePartnerType = (value) => {
              const trimmedValue = (value || '').trim();
              setUpdatedFields((prev) => {
                const next = { ...prev, partner_type: trimmedValue };
                if (trimmedValue !== 'Merchant') {
                  next.service_type = [];
                }
                return next;
              });
              setFormErrors((prev) => ({ ...prev, partner_type: '', service_type: '' }));
            };
            return (
              <Grid item xs={12} md={6} key={item.field}>
                <Autocomplete
                  freeSolo
                  options={partnerTypeOptions}
                  value={currentPartnerType}
                  onChange={(_, newValue) => updatePartnerType(newValue)}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      updatePartnerType(newInputValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label={item.label}
                      variant="outlined"
                      helperText="Select or type a new partner type"
                      InputProps={{
                        ...params.InputProps,
                        style: { height: '45px' },
                      }}
                    />
                  )}
                />
              </Grid>
            );
          }

          // Multi-select Service Type for Partner accounts (configurable freeSolo)
          if (item.field === 'service_type') {
            const currentPartnerType = (updatedFields.partner_type ?? account?.partner_type ?? '').toString().trim();
            const isMerchantPartner = currentPartnerType === 'Merchant';
            const currentServiceTypes = updatedFields.service_type ?? account?.service_type ?? [];
            const serviceValues = Array.isArray(currentServiceTypes)
              ? currentServiceTypes
              : (currentServiceTypes ? String(currentServiceTypes).split(',').map(s => s.trim()).filter(Boolean) : []);
            displayValue = isMerchantPartner ? serviceValues.join(', ') : '-';

            if (isEditing) {
              return (
                <Grid item xs={12} md={6} key={item.field}>
                  <Autocomplete
                    multiple
                    freeSolo
                    disabled={!isMerchantPartner}
                    options={serviceTypeOptions}
                    value={isMerchantPartner ? serviceValues : []}
                    onChange={(_, newValue) => {
                      const cleaned = (newValue || [])
                        .map((v) => (typeof v === 'string' ? v.trim() : v))
                        .filter(Boolean);
                      const unique = [];
                      cleaned.forEach((v) => {
                        if (!unique.some((u) => u.toLowerCase() === v.toLowerCase())) {
                          unique.push(v);
                        }
                      });
                      handleFieldUpdate('service_type', unique);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label={item.label}
                        variant="outlined"
                        required={isMerchantPartner}
                        error={!!formErrors.service_type}
                        helperText={
                          formErrors.service_type ||
                          (isMerchantPartner
                            ? 'Select or type service types (required for Merchant)'
                            : 'Available when Partner Type is Merchant')
                        }
                      />
                    )}
                  />
                </Grid>
              );
            }
          }

          // Format state code as two digits
          if (item.field === 'billing_stcd' || item.field === 'shipping_stcd') {
            displayValue = formatStateCode(account?.[item.field] ?? updatedFields[item.field]);
          }

          return (
            <Grid item xs={12} md={6} key={item.field}>
              {item.field === 'created_by_name' || item.field === 'updated_by_name' ? (
                <InlineEditField
                  label={item.label}
                  value={capitalizeFirstLetter(displayValue)}
                  isEditing={isEditing}
                  readOnly={item.readOnly ?? false}
                  renderValue={(value) => (
                    <MDTypography
                      component="span"
                      onClick={() => {
                        const userId = item.field === 'created_by_name' ? account?.created_by : account?.updated_by;
                        if (userId) {
                          navigate(`/users/${userId}/profile`);
                        }
                      }}
                      variant="button"
                      color="info"
                      fontWeight="medium"
                      sx={{
                        cursor: 'pointer'
                      }}
                    >
                      {capitalizeFirstLetter(value)}
                    </MDTypography>
                  )}
                />
              ) : (
                <InlineEditField
                  label={item.label}
                  value={['tax_id', 'notes', 'email', 'currency', 'partner_code', 'trading_name', 'business_registration_number', 'service_type'].includes(item.field)
                    ? displayValue // Show as entered
                    : (isEditing && item.field === 'annual_revenue'
                      ? account?.[item.field]
                      : typeof displayValue === 'string' && !['created_at', 'updated_at', 'annual_revenue', 'number_of_employees'].includes(item.field)
                        ? capitalizeFirstLetter(displayValue)
                        : displayValue
                    )
                  }
                  options={
                    item.field === 'manager_id'
                      ? agents.map(agent => ({ value: agent.id, label: capitalizeFirstLetter(agent.fullname) }))
                      : item.options ? item.options.map(opt => capitalizeFirstLetter(opt)) : null
                  }
                  onSave={handleSave}
                  isEditing={isEditing}
                  readOnly={item.readOnly ?? false}
                  currency={item.field === 'annual_revenue' ? (account?.currency || systemCurrency) : null}
                  renderValue={
                    item.field === 'manager_id'
                      ? (value) => {
                        const agent = agents.find(agent => agent.id === value);
                        return (
                          <MDTypography
                            component="span"
                            onClick={() => handleAgentClick(value)}
                            variant="button"
                            color="info"
                            fontWeight="medium"
                            sx={{ cursor: 'pointer' }}
                          >
                            {agent ? capitalizeFirstLetter(agent.fullname) : 'Unassigned'}
                          </MDTypography>
                        );
                      }
                      : item.field === 'created_by_name' || item.field === 'updated_by_name'
                        ? (value) => (
                          <MDTypography
                            component="span"
                            onClick={() => {
                              const userId = item.field === 'created_by_name' ? account?.created_by : account?.updated_by;
                              if (userId) {
                                navigate(`/users/${userId}/profile`);
                              }
                            }}
                            variant="button"
                            color="info"
                            fontWeight="medium"
                            sx={{ cursor: 'pointer' }}
                          >
                            {value}
                          </MDTypography>
                        )
                        : ['tax_id', 'notes', 'email', 'currency'].includes(item.field)
                          ? (value) => value // Show as entered, preserve case
                          : (value) =>
                            typeof value === 'string' && !['created_at', 'updated_at', 'annual_revenue', 'number_of_employees'].includes(item.field)
                              ? capitalizeFirstLetter(value)
                              : value
                  }
                  required={item.field === 'name' || item.required === true}
                  error={item.field === 'trading_name' ? (formErrors.trading_name || '') : ''}
                  helperText={
                    item.field === 'name' && !account?.name
                      ? 'Name field is required.'
                      : undefined
                  }
                />
              )}
            </Grid>
          );
        })}
      </Grid>
      {renderContent && (
        <MDBox mt={3}>
          {renderContent}
        </MDBox>
      )}
      </>
    );
    return (
      <UiDetailSection
        title={title}
        legacy={
          <MDBox
            mb={3}
            p={3}
            borderRadius="lg"
            sx={{
              backgroundColor: 'white',
              boxShadow: 3
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium" color="dark">{title}</MDTypography>
            </MDBox>
            {sectionBody}
          </MDBox>
        }
      >
        {sectionBody}
      </UiDetailSection>
    );
  };

  // Add this function to fetch user details
  const fetchUserDetails = async (userId) => {
    try {
      const response = await axiosInstance.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return null;
    }
  };

  // Common icon button styles - add this near the top of your component
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get(`/custom-fields/accounts`);
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);

  const renderCustomFields = () => {
    if (!customFields || customFields.length === 0) {
      return null;
    }
    return (
      <CustomFieldsSection
        ref={customFieldsRef}
        objectType="accounts"
        objectData={account}
        isEditing={isEditing}
        onFieldUpdate={(field, value) => {
          handleFieldUpdate(field, value);
        }}
        onError={(field, error) => {
          if (error) {
            handleFieldError(field, error);
          }
        }}
        formErrors={formErrors}
      />
    );
  };

  // Toggle functions for CC and BCC fields
  const toggleCc = () => setShowCc(!showCc);
  const toggleBcc = () => setShowBcc(!showBcc);

  // Function to handle Quill editor changes
  const handleQuillChange = (content) => {
    setNewEmail({ ...newEmail, content });
  };

  // Define Quill editor modules and formats
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link', 'image',
  ];


  const handleToggleEmailModal = async () => {
    setIsEmailLoading(true);
    if (!isEmailModalOpen) {
      if (!account) {
        setSnackbar({ open: true, message: 'Account details not loaded.', severity: 'error' });
        setIsEmailLoading(false);
        return;
      }
      if (!account.email || account.email.trim() === '') {
        setSnackbar({ open: true, message: 'No email address linked.', severity: 'warning' });
        setIsEmailLoading(false);
        return;
      }
      try {
        const emailSubject = `Regarding ${account.name} - Account Information`;
        const emailContent = ``;
        setNewEmail({ to: account.email, cc: '', bcc: '', subject: emailSubject, content: emailContent, from: user.email });
        setEmailContext({ type: 'account', data: account, deals, contacts, activities, orders, cases, sender: user });
        setEmailAttachments([]);
        setIsEmailModalOpen(true);
      } catch (error) {
        console.error('Error preparing email:', error);
        setSnackbar({ open: true, message: 'Error preparing email.', severity: 'error' });
      } finally {
        setIsEmailLoading(false);
      }
    } else {
      setIsEmailModalOpen(false);
      setIsEmailLoading(false);
    }
  };

  const handle360View = () => {
    setIs360Loading(true);
    try {
      navigate(`/account/360/${id}`);
    } finally {
      setIs360Loading(false);
    }
  };

  // Function to generate email content using AI
  const handleGenerateEmail = async () => {
    setIsGeneratingEmail(true);
    setEmailError('');

    try {
      // Check if sender's email is provided
      // if (!user?.email) {
      //   throw new Error('Sender email address is missing. Please update your profile with a valid email address.');
      // }

      // Implementation for AI email generation
      const context = emailContext;
      let generatedContent = '';
      let subject = '';

      if (context.type === 'account') {
        const accountData = context.data;
        subject = `Account Update: ${accountData.name}`;
        generatedContent = `<p>Dear ${accountData.contact_person || 'Customer'},</p>
        <p>I hope this email finds you well. I'm reaching out regarding your account with us.</p>
        <p>We value our partnership with ${accountData.name} and wanted to provide you with an update on your account status.</p>
        <p>Here's a summary of your recent activity:</p>
        <ul>
          <li>Open Deals: ${context.deals?.length || 0}</li>
          <li>Recent Orders: ${context.orders?.length || 0}</li>
          <li>Active Cases: ${context.cases?.length || 0}</li>
        </ul>
        <p>If you have any questions or require any assistance, please don't hesitate to reach out.</p>
        <p>Looking forward to our continued collaboration.</p>
        <p>Best regards,<br>${user?.name || 'Sales Team'}</p>`;
      }

      // Set the email content before sending
      const emailToSend = {
        ...newEmail,
        subject: subject || newEmail.subject,
        content: generatedContent || newEmail.content,
        // from_email: user?.email,
        // from_name: user?.name || 'Sales Team'
      };

      setNewEmail(emailToSend);

      // Call the API to send the email
      const response = await axiosInstance.post('/emails/send', {
        // subject: emailToSend.subject,
        // content: emailToSend.content,
        // to_email: emailToSend.to,
        // to_name: emailToSend.to_name,
        // from_email: emailToSend.from_email,
        // from_name: emailToSend.from_name,
        context: context
      });

      if (response.status === 200) {
        // Success handling
        console.success('Email sent successfully!');
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error generating or sending email:', error);

      if (error.response) {
        console.error('Error data:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
        setEmailError(`Server error: ${error.response.data.detail || error.message}`);
      } else if (error.request) {
        console.error('Error request:', error.request);
        setEmailError('No response received from server. Please try again.');
      } else {
        console.error('Error message:', error.message);
        setEmailError(`Error: ${error.message}`);
      }
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  // Add this helper function near the top of your component
  const formatTimeForPayload = (date) => {
    return date.toTimeString().split(' ')[0]; // Returns time in HH:MM:SS format
  };

  // Add this helper function near other utility functions
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // First replace <br>, <p>, and other block elements with newlines
    const textWithNewlines = html
      .replace(/<\/(p|div|br|h[1-6])>/gi, '\n')
      .replace(/<br\/?>/gi, '\n');
    // Then remove all HTML tags and decode HTML entities
    const div = document.createElement('div');
    div.innerHTML = textWithNewlines;
    return div.textContent || div.innerText || '';
  };

  // Update the handleSendEmail function
  const handleSendEmail = async () => {
    setIsEmailSending(true);
    try {
      if (!newEmail.to || !newEmail.subject || !newEmail.content) {
        const missingFields = [];
        if (!newEmail.to) missingFields.push('To');
        if (!newEmail.subject) missingFields.push('Subject');
        if (!newEmail.content) missingFields.push('Content');
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const emailPayload = {
        to: newEmail.to,
        subject: newEmail.subject,
        content: newEmail.content,
        cc: newEmail.cc || '',
        bcc: newEmail.bcc || '',
        parent_table: 'accounts',
        parent_table_id: id
      };

      // Send email
      const emailResponse = await axiosInstance.post('/emails/send', emailPayload);

      // After email is sent successfully, create an activity with proper time formatting
      const now = new Date();
      const isoDate = now.toISOString();
      const formattedTime = formatTimeForPayload(now);
      const cleanDescription = stripHtmlTags(newEmail.content);

      const activityPayload = {
        title: `Email: ${newEmail.subject}`,
        type: "Email",
        status: "Completed",
        description: cleanDescription,
        starttime: formattedTime,
        endtime: formattedTime,
        due_date: isoDate,
        parent_table: "accounts",
        parent_table_id: id,
        assigned_to: user.id,
        assigned_to_name: user.fullname,
        all_day: false,
        color: "#edc949"
      };

      // Create activity
      const activityResponse = await axiosInstance.post('/activities/', activityPayload);

      if (!activityResponse.data) {
        throw new Error('Failed to create activity record');
      }

      setSnackbar({
        open: true,
        message: 'Email sent and activity created successfully',
        severity: 'success',
      });

      setEmailAttachments([]);
      setIsEmailModalOpen(false);

      // Refresh activities list
      await fetchActivities();

    } catch (error) {
      console.error('Error sending email or creating activity:', error);
      let errorMessage = 'Error sending email';

      if (error.response) {
        const { status, data } = error.response;
        errorMessage = typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail[0]?.msg || 'Unknown error'
            : 'Server error';

        if (status === 404 && data.detail === 'Email settings not found') {
          errorMessage = 'Email settings not configured. Please set up your email provider in settings.';
        } else if (status === 400) {
          errorMessage = 'Invalid request. Please check your input.';
        } else if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        }
      } else if (error.request) {
        errorMessage = 'Failed to connect to the server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsEmailSending(false);
    }
  };

  // Fetch global preferences for section order/hidden
  useEffect(() => {
    const fetchGlobalPreferences = async () => {
      try {
        const res = await axiosInstance.get('/preferences/global');
        const prefs = res.data?.section_preferences?.account;
        if (prefs) {
          if (prefs.order) {
            const order = [...prefs.order];
            if (!order.includes('Partner Details')) {
              const basicIdx = order.indexOf('Basic Information');
              order.splice(basicIdx >= 0 ? basicIdx + 1 : 1, 0, 'Partner Details');
            }
            setSectionOrder(order);
          }
          if (prefs.hidden) setHiddenSections(prefs.hidden);
        } else {
          // fallback to default order
          setSectionOrder([
            'Basic Information',
            'Partner Details',
            'Financial Information',
            'Billing Address',
            'Shipping Address',
            'Custom Fields',
            'Audit Information',
          ]);
          setHiddenSections([]);
        }
      } catch (e) {
        // fallback to default order
        setSectionOrder([
          'Basic Information',
          'Partner Details',
          'Financial Information',
          'Contact Details',
          'Billing Address',
          'Shipping Address',
          'Custom Fields',
          'Audit Information',
        ]);
        setHiddenSections([]);
      } finally {
        setLoadingSections(false);
      }
    };
    fetchGlobalPreferences();
    // eslint-disable-next-line
  }, []);

  // Compose visible sections in order — Partner Details only for Partner accounts when Travel is on
  const visibleSections = sectionOrder
    .filter(key => !hiddenSections.includes(key))
    .filter(key =>
      key !== 'Partner Details'
      || (showTravel && account?.account_type === 'Partner'),
    );

  // if (isLoading) {
  //   return (
  //     <DashboardLayout>
  //       <DashboardNavbar />
  //       <DataLoadingSpinner
  //         entityType="account"
  //         title="Loading Account Data"
  //         message="Please wait while we fetch the account information..."
  //       />
  //       
  //     </DashboardLayout>
  //   );
  // }

  // Check if the user has permission to access this lead
  if (isUnauthorized) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <UnauthorizedAccess />
        
      </DashboardLayout>
    );
  }

  const fetchFeedItems = async () => {
    setIsFeedLoading(true);
    try {
      const response = await axiosInstance.get(`/feed-items/entity/accounts/${id}`);
      setFeedItems(response.data || []);
    } catch (error) {
      console.error('Error fetching feed items:', error);
      setFeedItems([]);
    } finally {
      setIsFeedLoading(false);
    }
  };

  const handleEchoSubmit = async (feedItem) => {
    // Add the new feed item to the beginning of the list
    setFeedItems(prevItems => [feedItem, ...prevItems]);

    // Close the modal
    handleCloseEchoModal();

    // Show success message
    setSnackbar({
      open: true,
      message: 'Message posted successfully!',
      severity: 'success',
    });

    // Refresh the feed items to ensure we have the latest data
    await fetchFeedItems();
  };

  const handleEditFeedItem = async (editData) => {
    try {
      setIsEditingFeed(true);

      // Extract the feed item ID from the edit data
      const feedItemId = editData.feed_item_id;

      if (!feedItemId) {
        throw new Error('No feed item ID found in edit data');
      }

      console.log('=== EDIT FEED ITEM DEBUG ===');
      console.log('Feed Item ID:', feedItemId);
      console.log('Edit Data received:', editData);
      console.log('Object Type:', editData.object_type);
      console.log('Object ID:', editData.object_id);
      console.log('Body content:', editData.body);
      console.log('Mentions:', editData.mentions);

      // Make the PUT request to the correct endpoint with the feed item ID
      const response = await axiosInstance.put(`/feed-items/${feedItemId}`, {
        feeditem: {
          object_type: editData.object_type,
          object_id: editData.object_id,
          body: editData.body
          // Don't include mentions - backend will parse them from body automatically
        }
      });

      if (response.status === 200) {
        // Refresh the entire feed to get updated data and proper sorting
        await fetchFeedItems();

        setSnackbar({
          open: true,
          message: 'Message updated successfully!',
          severity: 'success',
        });

        // The EchoComposer will handle clearing the edit state via onSuccess callback
        // so we don't need to do it here
      }
    } catch (error) {
      console.error('Error updating feed item:', error);
      // setSnackbar({
      //   open: true,
      //   message: 'Error updating message',
      //   severity: 'error',
      // });
    } finally {
      setIsEditingFeed(false);
    }
  };

  const handleDeleteFeedItem = async (feedItemId) => {
    try {
      const response = await axiosInstance.delete(`/feed-items/${feedItemId}`);

      if (response.status === 200) {
        // Update the feed items state by removing the deleted item
        setFeedItems(prevItems => prevItems.filter(item => item.id !== feedItemId));

        setSnackbar({
          open: true,
          message: 'Message deleted successfully!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error deleting feed item:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting message',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = (feedItem) => {
    setItemToDelete(feedItem);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirmOk = async () => {
    if (itemToDelete) {
      await handleDeleteFeedItem(itemToDelete.id);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const startEditFeedItem = (feedItem) => {
    console.log('=== START EDIT FEED ITEM ===');
    console.log('Feed Item ID:', feedItem.id);
    console.log('Feed Item Data:', feedItem);
    console.log('Setting editingFeedItem to:', feedItem.id);
    console.log('Setting editFeedContent to:', feedItem.feeditem.body);
    setEditingFeedItem(feedItem.id);
    setEditFeedContent(feedItem.feeditem.body);
  };

  const cancelEditFeedItem = () => {
    setEditingFeedItem(null);
    setEditFeedContent('');
  };

  const handleOpenEchoModal = () => {
    setIsEchoModalOpen(true);
  };

  const handleCloseEchoModal = () => {
    setIsEchoModalOpen(false);
  };

  const handleCreateEcho = async () => {
    // This will be handled by EchoComposer's onSubmit
    handleCloseEchoModal();
  };

  // Component for rendering mentions with immediate name display
  const MentionDisplay = ({ mention, fullMatch, entityTypeOrUser, entityId, mentions, navigate }) => {
    // Calculate display name immediately without async operations
    let displayName = '';

    if (entityId) {
      // For entity mentions, we'll need to fetch the name
      // But for now, let's show the entity type and ID
      displayName = `${entityTypeOrUser}:${entityId}`;
    } else {
      // For user mentions, use entity_name immediately if available
      if (mention && mention.entity_name) {
        displayName = mention.entity_name;
      } else {
        // Fallback to username
        displayName = entityTypeOrUser;
      }
    }

    return (
      <Box
        component="span"
        sx={{
          color: entityId ? 'info.main' : 'primary.main',
          fontWeight: 'bold',
          backgroundColor: entityId ? 'rgba(33, 150, 243, 0.08)' : 'rgba(103, 58, 183, 0.08)',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          mx: 0.25,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: entityId ? 'rgba(33, 150, 243, 0.15)' : 'rgba(103, 58, 183, 0.15)',
          }
        }}
        onClick={() => {
          if (entityId) {
            // Navigate to the mentioned entity
            navigate(`/${entityTypeOrUser.toLowerCase()}s/${entityId}`);
          } else {
            // Handle user mention - navigate to user profile
            if (mention && mention.entity_id) {
              navigate(`/users/${mention.entity_id}/profile`);
            }
          }
        }}
      >
        {displayName}
      </Box>
    );
  };

  // Helper function to extract name parts from created_by_name
  const extractNameParts = (createdByUser, createdByName) => {
    let nameParts = {
      firstName: 'U',
      middleName: '',
      lastName: '',
      avatarUrl: null
    };

    if (createdByUser?.first_name && createdByUser?.last_name) {
      nameParts = {
        firstName: createdByUser.first_name,
        middleName: createdByUser.middle_name || '',
        lastName: createdByUser.last_name,
        avatarUrl: createdByUser.avatar || null
      };
    } else if (createdByName) {
      const namePartsArray = createdByName.split(' ').filter(part => part.trim());
      if (namePartsArray.length === 1) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: '',
          lastName: '',
          avatarUrl: null
        };
      } else if (namePartsArray.length >= 2) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: namePartsArray.slice(1, -1).join(' '),
          lastName: namePartsArray[namePartsArray.length - 1],
          avatarUrl: null
        };
      }
    }

    return nameParts;
  };

  // Enhanced feed content parser that uses async name fetching
  const parseFeedContent = (feedItem) => {
    if (!feedItem?.feeditem?.body) return '';

    const body = feedItem.feeditem.body;
    const mentions = feedItem.feeditem.mentions || [];

    console.log('Parsing feed content:', { body, mentions });

    // Parse mentions in the format @username (for users) or @EntityType:entityId (for entities)
    const parts = [];
    const mentionRegex = /@([A-Za-z0-9_.@]+)(?::([A-Za-z0-9_]+))?/g;
    let lastIndex = 0;
    let match;

    // Create a map of usernames to mention data for better matching
    const userMentionsMap = {};
    mentions.forEach(mention => {
      if (mention.type === 'user' && mention.entity_name) {
        // Try to match by username pattern in the body
        const usernamePattern = new RegExp(`@${mention.entity_name.replace(/\s+/g, '[._]')}`, 'i');
        if (usernamePattern.test(body)) {
          userMentionsMap[mention.entity_name] = mention;
        }
      }
    });

    while ((match = mentionRegex.exec(body)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {body.substring(lastIndex, match.index)}
          </span>
        );
      }

      // Add mention as highlighted text with async name fetching
      const [fullMatch, firstPart, secondPart] = match;

      // Determine if this is a user mention or entity mention
      let entityTypeOrUser, entityId;

      if (secondPart) {
        // Format: @EntityType:entityId
        entityTypeOrUser = firstPart;
        entityId = secondPart;
      } else {
        // Format: @username (user mention)
        entityTypeOrUser = firstPart;
        entityId = null;
      }

      // Find the corresponding mention data
      let mentionData;
      if (entityId) {
        // For entity mentions: @EntityType:entityId
        mentionData = mentions.find(m => m.type === 'record' && m.entity_type === entityTypeOrUser && m.entity_id === entityId);
      } else {
        // For user mentions: @username - try multiple matching strategies
        // First, try exact match with entity_name
        mentionData = mentions.find(m => m.type === 'user' && m.entity_name === entityTypeOrUser);

        // If not found, try matching by username pattern
        if (!mentionData) {
          const userMention = mentions.find(m => {
            if (m.type === 'user' && m.entity_name) {
              // Check if the username in text matches the entity_name (with possible dots/underscores)
              const normalizedEntityName = m.entity_name.replace(/\s+/g, '[._]');
              const usernamePattern = new RegExp(`^${normalizedEntityName}$`, 'i');
              return usernamePattern.test(entityTypeOrUser);
            }
            return false;
          });
          if (userMention) {
            mentionData = userMention;
          }
        }

        // If still not found, try to find by position in the mentions array
        if (!mentionData) {
          const userMentions = mentions.filter(m => m.type === 'user');
          // Count how many user mentions we've seen so far in the text
          const textBeforeCursor = body.substring(0, match.index);
          const userMentionsBefore = (textBeforeCursor.match(/@[A-Za-z0-9_.@]+/g) || []).length;
          if (userMentionsBefore < userMentions.length) {
            mentionData = userMentions[userMentionsBefore];
          }
        }
      }

      parts.push(
        <MentionDisplay
          key={`mention-${match.index}`}
          mention={mentionData}
          fullMatch={fullMatch}
          entityTypeOrUser={entityTypeOrUser}
          entityId={entityId}
          mentions={mentions}
          navigate={navigate}
        />
      );

      lastIndex = match.index + fullMatch.length;
    }

    // Add remaining text
    if (lastIndex < body.length) {
      parts.push(
        <span key={`text-end`}>
          {body.substring(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  // Shared by the classic and v2 layouts below: the same actions, field
  // sections and side column render in both.
  const recordActions = (
    <>
      {!isEditing && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Renders nothing when telephony is not configured,
              so this is safe on every tenant. */}
          <CallContactMenu
            targets={callTargets}
            parentTable="accounts"
            parentId={account?.id}
            accountId={account?.id}
            tone="header"
          />
          {/* v2: this page is the Account 360, and /account/360 redirects here. */}
          {!isV2 && (
          <Tooltip title="Customer 360° View">
            <IconButton onClick={handle360View} sx={commonIconButtonStyles}>
              {is360Loading ? <CircularProgress size={20} color="inherit" /> : <ThreeSixty sx={{ color: '#ffffff', fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
          )}
          <Tooltip title="Send Email">
            <IconButton onClick={handleToggleEmailModal} sx={commonIconButtonStyles}>
              {isEmailLoading ? <CircularProgress size={20} color="inherit" /> : <Email sx={{ color: '#ffffff', fontSize: 20 }} />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit Account">
            <IconButton onClick={handleEditToggle} sx={commonIconButtonStyles}>
              {isEditLoading ? <CircularProgress size={20} color="inherit" /> : <Edit sx={{ color: '#ffffff', fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Clone Account">
            <IconButton onClick={handleCloneClick} sx={commonIconButtonStyles}>
              {isCloneLoading ? <CircularProgress size={20} color="inherit" /> : <FileCopy sx={{ color: '#ffffff', fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Account">
            <IconButton onClick={handleOpenDeleteModal} sx={commonIconButtonStyles}>
              {isDeleteLoading ? <CircularProgress size={20} color="inherit" /> : <Delete sx={{ color: '#ffffff', fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {isEditing && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <MDButton
            variant="contained"
            onClick={handleDiscardChanges}
            sx={{ background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)', color: '#ffffff' }}
            startIcon={isCancelLoading ? <CircularProgress size={20} color="inherit" /> : <Close />}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            onClick={handleSaveChanges}
            sx={{ background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)', color: '#ffffff' }}
            startIcon={isSaveLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
          >
            Save
          </MDButton>
        </Box>
      )}
    </>
  );
  const recordDetailsBody = (
    <>
      {isDetailsLoading ? (
        <AccountDetailsSkeleton />
      ) : (
        <>
          {visibleSections.map(sectionLabel => {
            if (sectionLabel === 'Basic Information') {
              return renderSection('Basic Information', [
                { label: "Name", field: "name" },
                { label: "Owner", field: "manager_id" },
                {
                  label: "Account Type",
                  field: "account_type",
                  // Keep the current value in the list when travel is off
                  // so existing Partner accounts don't show a blank Select.
                  options: (() => {
                    const base = showTravel
                      ? ["Company", "Partner", "Individual"]
                      : ["Company", "Individual"];
                    const current =
                      updatedFields.account_type ?? account?.account_type;
                    if (current && !base.includes(current)) {
                      return [...base, current];
                    }
                    return base;
                  })(),
                },
                { label: "Industry", field: "industry", options: ["Automotive", "Banking", "Construction", "Education", "Financial Services", "Government", "Healthcare", "Manufacturing", "Media", "Retail", "Technology", "Travel", "Other"] },
                { label: "Account Status", field: "account_status", options: ["Active", "Inactive"] },
                { label: "Website", field: "website" },
                { label: "Phone", field: "phone" },
                { label: "Mobile", field: "mobile" },
                { label: "Email", field: "email" },
                { label: "Fax", field: "fax" },
                { label: "Tax ID", field: "tax_id" },
                { label: "Notes", field: "notes" },
              ]);
            }
            if (sectionLabel === 'Partner Details' && showTravel && account?.account_type === 'Partner') {
              return renderSection('Partner Details', [
                { label: "Partner Code", field: "partner_code", readOnly: true },
                { label: "Partner Type", field: "partner_type" },
                { label: "Service Type", field: "service_type", multiple: true },
                { label: "Trading Name", field: "trading_name", required: Boolean((account?.trading_name || '').toString().trim()) },
                { label: "Business Registration Number", field: "business_registration_number" },
                { label: "Country of Registration", field: "country_of_registration" },
              ]);
            }
            if (sectionLabel === 'Financial Information') {
              return renderSection('Financial Information', [
                { label: "Annual Revenue", field: "annual_revenue" },
                { label: "Currency", field: "currency" },
                { label: "Number of Employees", field: "number_of_employees" },
              ]);
            }
            if (sectionLabel === 'Billing Address') {
              return renderSection('Billing Address', [
                { label: "Address", field: "billing_address" },
                { label: "City", field: "billing_city" },
                { label: "State", field: "billing_state" },
                { label: "State Code", field: "billing_stcd" },
                { label: "Country", field: "billing_country" },
                { label: "Postal Code", field: "billing_postal_code" },
              ]);
            }
            if (sectionLabel === 'Shipping Address') {
              return renderSection('Shipping Address', [
                { label: "Address", field: "shipping_address" },
                { label: "City", field: "shipping_city" },
                { label: "State", field: "shipping_state" },
                { label: "State Code", field: "shipping_stcd" },
                { label: "Country", field: "shipping_country" },
                { label: "Postal Code", field: "shipping_postal_code" },
              ]);
            }
            if (sectionLabel === 'Custom Fields') {
              return renderSection('Custom Fields', [], renderCustomFields());
            }
            if (sectionLabel === 'Audit Information') {
              return renderSection('Audit Information', [
                { label: "Created By", field: "created_by_name", readOnly: true },
                { label: "Modified By", field: "updated_by_name", readOnly: true },
                { label: "Created Date", field: "created_at", readOnly: true },
                { label: "Modified Date", field: "updated_at", readOnly: true },
              ]);
            }
            return null;
          })}
        </>
      )}
    </>
  );
  // The side column's cards, one variable each, so the v2 Account 360 can
  // place them in its tabs while the classic layout keeps rendering them in
  // this order. Moving JSX into variables changes nothing on screen.
  const activitiesCard = (
    <>
      {hasTasksModule && (
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Activities
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={handleOpenActivityModal}>
            Add Activity
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          {isActivitiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} color="info" />
            </Box>
          ) : (
            <Activities
              activities={activities.filter(Boolean)}
              onDelete={handleDeleteActivity}
              onEdit={handleEditActivity}
            />
          )}
        </MDBox>
      </Card>
      )}
    </>
  );
  const attachmentsCard = (
    <>
      <Card style={{ marginTop: '10px' }}>
        <MDBox
          mx={2}
          mt={-2}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachmentIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Attachments
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={() => setIsAttachmentModalOpen(true)}>
            Add Attachment
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          <Attachments
            attachments={attachments}
            onDelete={handleDeleteAttachment}
            onDownload={handleDownloadAttachment}
          />
        </MDBox>
      </Card>
    </>
  );
  // Recordings and transcripts for this record. Gates itself on the
  // CALL_CENTER entitlement, so no check is needed here.
  const callHistoryCard = (
    <>
      <CallHistoryCard recordType="account" recordId={id} />
    </>
  );
  const echoFeedCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RecordVoiceOverIcon sx={{ color: 'white !important', mr: 1 }} />

            <MDTypography variant="h6" color="white">
              Echo Feed
            </MDTypography>
          </Box>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={handleOpenEchoModal}
          >
            Add Message
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          {/* Echo Modal */}
          <Modal
            open={isEchoModalOpen}
            onClose={handleCloseEchoModal}
            aria-labelledby="echo-modal-title"
            aria-describedby="echo-modal-description"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              maxWidth: '90vw',
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: 2,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
              <Box sx={{
                p: 1,
                display: 'flex',
                flexDirection: 'column',
              }}>
                <MDTypography id="echo-modal-title" variant="h6" mb={2}>
                  Post a Feed
                </MDTypography>
                <EchoComposer
                  onSubmit={handleEchoSubmit}
                  onCancel={handleCloseEchoModal}
                  placeholder="Type @ to mention users..."
                  axiosInstance={axiosInstance}
                  objectType="accounts"
                  objectId={id}
                  onSuccess={(message) => {
                    setSnackbar({
                      open: true,
                      message: 'Feed item created successfully',
                      severity: 'success',
                    });
                    handleCloseEchoModal();
                  }}
                  onError={(message) => setSnackbar({
                    open: true,
                    message: `Error creating feed item: ${message}`,
                    severity: 'error',
                  })}
                />
              </Box>
            </Box>
          </Modal>
          {/* Display feed items */}
          <Box mt={2}>
            {isFeedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} color="info" />
              </Box>
            ) : feedItems.length > 0 ? (
              <Box className="pearl-echo-feed" sx={{
                maxHeight: 400,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa'
              }}>
                {feedItems.map((item, index) => (
                  <Box
                    key={item.id}
                    sx={{
                      p: 2,
                      borderBottom: index < feedItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <MDUserAvatar
                        firstName={extractNameParts(item.created_by_user, item.created_by_name).firstName}
                        middleName={extractNameParts(item.created_by_user, item.created_by_name).middleName}
                        lastName={extractNameParts(item.created_by_user, item.created_by_name).lastName}
                        avatarUrl={extractNameParts(item.created_by_user, item.created_by_name).avatarUrl}
                        size={36}
                        variant="circular"
                        sx={{ mr: 2, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, ml: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight="medium"
                              sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => {
                                // Navigate to user profile
                                const userMention = item.feeditem.mentions?.find(m => m.type === 'user' && m.entity_name === item.created_by_name);
                                if (userMention) {
                                  navigate(`/users/${userMention.entity_id}/profile`);
                                }
                              }}
                            >
                              {extractNameParts(item.created_by_user, item.created_by_name).firstName} {extractNameParts(item.created_by_user, item.created_by_name).lastName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              {formatDateTime(item.updated_at)}
                            </Typography>
                          </Box>

                          {/* Edit/Delete buttons - only show for current user's messages */}
                          {item.created_by === user?.id && (
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                              <Tooltip title="Edit message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="info"
                                  onClick={() => startEditFeedItem(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.25 }}
                                >
                                  <Edit sx={{ fontSize: '0.875rem' }} />
                                </MDButton>
                              </Tooltip>
                              <Tooltip title="Delete message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="error"
                                  onClick={() => handleDeleteConfirm(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.25 }}
                                >
                                  <Delete sx={{ fontSize: '0.875rem' }} />
                                </MDButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {editingFeedItem === item.id ? (
                          <Box sx={{ mt: 0.5 }}>
                            <EchoComposer
                              value={editFeedContent}
                              onChange={setEditFeedContent}
                              placeholder="Edit your message..."
                              axiosInstance={axiosInstance}
                              objectType="accounts"
                              objectId={id}
                              feedItemId={item.id}
                              isEditMode={true}
                              onSubmit={handleEditFeedItem}
                              onCancel={cancelEditFeedItem}
                              onSuccess={() => {
                                // Auto-save when user clicks the Update button in EchoComposer
                                setEditingFeedItem(null);
                                setEditFeedContent('');
                              }}
                              sx={{
                                mb: 0.5,
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'white'
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.5,
                              color: 'text.primary'
                            }}
                          >
                            {parseFeedContent(item)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mt: -2,
              }}>

                <Box sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.04)',
                  mb: 1.5
                }}>
                  <RecordVoiceOverIcon sx={{ color: 'text.secondary', fontSize: 24, }} />
                </Box>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  No messages yet
                </Typography>
                {/*  {/* <Typography variant="caption">
                  Start a conversation by typing a message above
                </Typography> */}
              </Box>
            )}
          </Box>
        </MDBox>
      </Card>
    </>
  );
  const contactsCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Contacts
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={handleOpenContactModal}>
            Add Contact
          </MDButton>
        </MDBox>
        <Box
          sx={{
            maxHeight: '250px',
            overflowY: 'auto',
            borderRadius: '4px',
            padding: '16px 24px',
            pb: 0.5,
            pt: 2,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '10px',
            },
          }}
        >
          {contacts.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 3,
                px: 2,
              }}
            >
              <Box sx={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.04)',
                mb: 1.5
              }}>
                <PeopleIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
              </Box>
              <Typography variant="body2" color="text.secondary" align="center">
                No contacts to display.
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                Create your first contact by clicking the "Add Contact" button above.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={1} sx={{ mt: 0 }}>
              {contacts
                .slice()
                .sort((a, b) => {
                  // Sort by primary contact first, then alphabetically
                  if (a.details.primary_contact && !b.details.primary_contact) return -1;
                  if (!a.details.primary_contact && b.details.primary_contact) return 1;
                  return a.name.localeCompare(b.name);
                })
                .map((contact, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <ContactCard
                        contactName={contact.name}
                        contactEmail={contact.details.email}
                        contactMobile={contact.mobile}
                        isPrimary={contact.details.primary_contact === true}
                        onClick={() => handleContactClick(contact)}
                        onCopyEmail={() => handleCopyContactInfo('email', contact.details.email)}
                        onCopyPhone={() => handleCopyContactInfo('phone', contact.mobile)}
                    />
                  </Grid>
                ))}
            </Grid>
          )}
        </Box>
      </Card>
    </>
  );
  const dealsCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocalOfferIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Deals
            </MDTypography>
          </Box>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={handleOpenDealModal}
            disabled={isDealLoading} // Optional: disable button while loading
            startIcon={isDealLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            Add Deal
          </MDButton>
        </MDBox>

        {/* Replace the old MDBox with our new DealsList component */}
        <DealsList
          deals={deals}
          onDealClick={handleDealClick}
        />
      </Card>
    </>
  );
  const ordersCard = (
    <>
      {showLegacyOrders && (
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ShoppingCartIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Orders
            </MDTypography>
          </Box>
          <MDButton variant="gradient"
            color="dark"
            onClick={handleOpenOrderModal}
            disabled={isOrderLoading} // Optional: disable button while loading
            startIcon={isOrderLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            Add Order
          </MDButton>
        </MDBox>

        {/* Replace the old Box with our new OrdersList component */}
        <OrdersList
          orders={orders}
          onOrderClick={(order) => navigate(`/orders/${order.id}`)}
        />
      </Card>
      )}
    </>
  );
  const casesCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SupportAgentIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Cases
            </MDTypography>
          </Box>
          <MDButton variant="gradient"
            color="dark"
            onClick={handleOpenCaseModal}
            disabled={isCaseLoading} // Optional: disable button while loading
            startIcon={isCaseLoading ? <CircularProgress size={20} color="inherit" /> : null}>
            Add Case
          </MDButton>
        </MDBox>

        {/* Replace the old Box with our new CasesList component */}
        <CasesList
          cases={cases}
          onCaseClick={(caseItem) => navigate(`/cases/${caseItem.id}`)}
        />
      </Card>
    </>
  );
  const merchantCards = (
    <>
      {showTravelMerchantSections &&
        account?.account_type === 'Partner' &&
        (updatedFields.partner_type || account?.partner_type) === 'Merchant' && (
        <>
          <Box mt={5} />
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={2}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PaymentIcon sx={{ color: 'white !important', mr: 1 }} />
                <MDTypography variant="h6" color="white">
                  Payment Terms
                </MDTypography>
              </Box>
              <MDButton variant="gradient" color="dark" disabled>
                New
              </MDButton>
            </MDBox>
            <Box sx={{ py: 3, px: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No payment terms configured yet.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Related Payment Terms for this Merchant will appear here.
              </Typography>
            </Box>
          </Card>

          <Box mt={5} />
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={2}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InventoryIcon sx={{ color: 'white !important', mr: 1 }} />
                <MDTypography variant="h6" color="white">
                  Merchant Assets
                </MDTypography>
              </Box>
              <MDButton
                variant="gradient"
                color="dark"
                onClick={() => setIsMerchantAssetModalOpen(true)}
              >
                New
              </MDButton>
            </MDBox>
            <MerchantAssetsList
              assets={merchantAssets}
              loading={isMerchantAssetsLoading}
              onAssetClick={(asset) => navigate(`/merchant-assets/${asset.id}`)}
            />
          </Card>

          <Box mt={5} />
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={2}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon sx={{ color: 'white !important', mr: 1 }} />
                <MDTypography variant="h6" color="white">
                  Merchant Allocations
                </MDTypography>
              </Box>
              <MDButton
                variant="gradient"
                color="dark"
                onClick={() => setIsMerchantAllocationModalOpen(true)}
              >
                New
              </MDButton>
            </MDBox>
            <MerchantAllocationsList
              allocations={merchantAllocations}
              loading={isMerchantAllocationsLoading}
              onAllocationClick={(allocation) => navigate(`/merchant-allocations/${allocation.id}`)}
            />
          </Card>
        </>
      )}
    </>
  );
  const attachmentUploadModal = (
    <>
      <AttachmentUploadModal
        isOpen={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
        onUpload={handleUploadSuccess}
        axiosInstance={axiosInstance}
        // allowedFileTypes="image/*,application/pdf"
        maxSizeInMB={5}
      />
    </>
  );
  const recordSideColumn = (
    <>
      {activitiesCard}

      {hasTasksModule && <Box mt={5} />}

      {attachmentsCard}

      <Box mt={5} />
      {callHistoryCard}

      {/* Echo Feed Section */}
      <Box mt={5} />
      {echoFeedCard}
      {/* Add margin between the two sections */}
      <Box mt={5} />
      {contactsCard}

      <Box mt={5} />

      {dealsCard}

      <Box mt={5} />

      {ordersCard}

      <Box mt={5} />

      {casesCard}

      {merchantCards}

      <Box mt={5} />


      {attachmentUploadModal}
    </>
  );

  // v2: the Account 360 places the same actions, field sections and cards.
  const v2Main = (
    <>
      <Account360V2
        accountId={id}
        account={account}
        agents={agents}
        contacts={contacts}
        deals={deals}
        cases={cases}
        activities={activities.filter(Boolean)}
        feedItems={feedItems}
        attachments={attachments}
        isCloning={isCloning}
        recordActions={recordActions}
        recordDetailsBody={recordDetailsBody}
        cards={{
          activities: activitiesCard,
          attachments: attachmentsCard,
          callHistory: callHistoryCard,
          echoFeed: echoFeedCard,
          contacts: contactsCard,
          deals: dealsCard,
          orders: ordersCard,
          cases: casesCard,
          merchant: merchantCards,
        }}
        gates={{
          showTravel: showTravelMerchantSections,
          showLegacyOrders,
          showMerchant:
            showTravelMerchantSections &&
            account?.account_type === 'Partner' &&
            (updatedFields.partner_type || account?.partner_type) === 'Merchant',
        }}
      />
      {attachmentUploadModal}
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? <RecordCallProvider value={{ parentTable: "accounts", parentId: account?.id, accountId: account?.id }}>{v2Main}</RecordCallProvider> : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                animation: isCloning ? 'cloneAnimation 1s ease' : 'none',
              }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography
                    variant="h6"
                    color="white"
                    sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      lineHeight: 1.35,
                      pr: 2,
                    }}
                  >
                    {capitalizeFirstLetter(account?.name)}
                  </MDTypography>

                  <MDBox>
                    {recordActions}
                  </MDBox>

                </MDBox>
              </MDBox>
              <MDBox p={3}>
                {recordDetailsBody}
              </MDBox>


            </Card>
          </Grid>

          {/* Add the Customer 360 View component below the main account details */}
          {!isLoading && account && (
            <MDBox mt={3}>
              {/* <Customer360View accountId={account.id} /> */}
            </MDBox>
          )}

          <Grid item xs={12} md={4}>
            {recordSideColumn}
          </Grid>

        </Grid>
      </MDBox>
      )}



      <AccountFormModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        onSave={handleCloneSave}
        managers={managers}
        currencies={currencies}
        countries={[]}
        loadingCountries={false}
        defaultCurrency={defaultCurrency}
        user={user}
        title="Clone Account"
        // Email/phone/mobile are unique across leads/contacts/accounts — leave
        // them blank so the user must enter new values (matches Contact clone).
        initialData={
          account
            ? {
                ...account,
                email: '',
                phone: '',
                mobile: '',
              }
            : null
        }
      />

      <AddContactModal
        isOpen={isContactModalOpen}
        onClose={handleCloseContactModal}
        accountId={id}
        accountName={account?.name}
        onContactAdded={fetchContacts}
      />

      <NewMerchantAssetModal
        isOpen={isMerchantAssetModalOpen}
        onClose={() => setIsMerchantAssetModalOpen(false)}
        onSave={handleSaveMerchantAsset}
        defaultMerchantId={id}
        lockMerchant
      />

      <NewMerchantAllocationModal
        isOpen={isMerchantAllocationModalOpen}
        onClose={() => setIsMerchantAllocationModalOpen(false)}
        onSave={handleSaveMerchantAllocation}
        defaultMerchantId={id}
        defaultCurrency={account?.currency || ''}
        lockMerchant
      />

      <ActivityModal
        isOpen={isActivityModalOpen}
        onClose={handleCloseActivityModal}
        parentId={id}
        parent="accounts"
        axiosInstance={axiosInstance}
        onActivitySaved={handleActivitySaved}
        currentUser={user}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="delete-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Delete Account
            </MDTypography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            <MDTypography
              id="delete-modal-description"
              variant="body1"
              mb={3}
              sx={{ color: '#34495e', fontSize: '16px', lineHeight: 1.6 }}
            >
              Are you sure you want to delete this account? This action cannot be undone.
            </MDTypography>

            {deleteDependencies && deleteDependencies.has_dependencies ? (
              <Box sx={{
                mb: 3,
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main',
                bgcolor: 'rgba(255, 193, 7, 0.05)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MDTypography variant="body1" color="warning.dark" fontWeight="medium" sx={{ fontSize: '14px' }}>
                    Dependencies Found ({deleteDependencies.total_dependencies})
                  </MDTypography>
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2, mb: 1 }}>
                  {Object.entries(deleteDependencies.dependencies).map(([key, count]) => (
                    <Box component="li" key={key} sx={{ mb: 0.5 }}>
                      <MDTypography variant="body2" color="warning.dark" sx={{ fontSize: '13px' }}>
                        {key.charAt(0).toUpperCase() + key.slice(1)}: {count}
                      </MDTypography>
                    </Box>
                  ))}
                </Box>
                <MDTypography variant="caption" color="warning.dark" sx={{ fontSize: '12px', fontStyle: 'italic' }}>
                  Remove these dependencies first to enable deletion
                </MDTypography>
              </Box>
            ) : (
              <Box sx={{
                mb: 4,
                p: 3,
                bgcolor: 'info.light',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'info.main',
              }}>
                <MDTypography variant="body1" color="info.dark" sx={{ fontSize: '15px', lineHeight: 1.6 }}>
                  <strong>Note:</strong> If this account has associated contacts, deals, orders, invoices, quotations, cases, activities, or attachments,
                  you will need to remove or reassign them first before deletion is allowed.
                </MDTypography>
              </Box>
            )}

            <MDBox display="flex" justifyContent="flex-end" mt={4}>
              <MDButton
                onClick={handleCloseDeleteModal}
                variant="outlined"
                color="secondary"
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDelete}
                variant="gradient"
                color="error"
                disabled={deleteDependencies?.has_dependencies || isCheckingDependencies}
                startIcon={isCheckingDependencies ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontSize: '14px',
                  fontWeight: '600',
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(244, 67, 54, 0.4)',
                  }
                }}
              >
                {isCheckingDependencies ? 'Checking...' : 'Delete'}
              </MDButton>
            </MDBox>

            {/* {deleteDependencies?.has_dependencies && (
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'warning.light'
              }}>
                <MDTypography 
                  variant="body2" 
                  color="warning.main" 
                  sx={{ 
                    textAlign: 'center', 
                    fontSize: '13px',
                    fontWeight: '500',
                    fontStyle: 'italic'
                  }}
                >
                  Delete button is disabled because account has dependencies. Remove them first to enable deletion.
                </MDTypography>
              </Box>
            )} */}
          </Box>
        </Box>
      </Modal>

      {/* Feed Item Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="feed-delete-modal-title"
        aria-describedby="feed-delete-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="feed-delete-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Delete Message
            </MDTypography>
          </Box>
          <Box sx={{ p: 4 }}>
            <MDTypography variant="body1" mb={3}>
              Are you sure you want to delete this message? This action cannot be undone.
            </MDTypography>
            <MDBox display="flex" justifyContent="flex-end">
              <MDButton
                onClick={handleDeleteCancel}
                variant="outlined"
                color="secondary"
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDeleteConfirmOk}
                variant="gradient"
                color="error"
              >
                Delete
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

      {showLegacyOrders && (
      <OrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        onSave={handleSaveOrder}
        dealId={account?.id}
        dealName={account?.name}
        user={user}
        preselectedAccount={accountData}
      />
      )}
      <AddCaseModal
        isOpen={isCaseModalOpen}
        onClose={handleCloseCaseModal}
        onSave={handleSaveCase}
        accountId={id}
        preselectedAccount={accountData} // Pass the account data
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      

      {/* Email Modal */}
      <EmailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        handleSend={handleSendEmail}
        isGenerating={isGeneratingEmail}
        isSending={isEmailSending}
        errorMessage={emailError}
        emailContext={emailContext}
        setEmailContext={setEmailContext}
        handleGenerateEmail={handleGenerateEmail}
        showCc={showCc}
        showBcc={showBcc}
        toggleCc={toggleCc}
        toggleBcc={toggleBcc}
        handleQuillChange={handleQuillChange}
        modules={quillModules}
        formats={quillFormats}
        attachments={emailAttachments}
        setEmailAttachments={setEmailAttachments}
      />

    </DashboardLayout>
  );
}

export default AccountDetail;